/**
 * Fixture data mirroring the shape of `/api/admin/*` responses.
 *
 * Used until the real endpoints land on the main Worker. Pages consume
 * these through `useXxxQuery` hooks in `api.ts` so swapping to fetch later
 * is a one-file change per endpoint.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-22-v1
 */
import type {
    CannedReply,
    TicketMessage,
    AcceptanceLogEntry,
    ActivityEvent,
    AdminAuditEntry,
    AdminAuditKpis,
    AdminUser,
    AdminUserRow,
    AiKpis,
    AiModelUsage,
    AiTopUser,
    Alert,
    AnalyticsEvent,
    AnalyticsKpis,
    AudioAccuracy,
    AudioCampaign,
    AudioFailureRow,
    AudioFormatRow,
    AudioKpis,
    BackgroundJob,
    BillingHealthRow,
    BillingKpis,
    BillingTransaction,
    BroadcastRow,
    ByokHealth,
    ChangelogEntry,
    ChangelogKpis,
    ChannelDelivery,
    CohortRow,
    ComplianceKpis,
    DauPoint,
    DbPoolDetail,
    DropboxTokenHealth,
    ElectronVersionBucket,
    ErrorLogRow,
    FeatureAdoption,
    FeatureFlagKpis,
    FeatureFlagRow,
    FunnelStep,
    GdprExportRow,
    GeoRow,
    HealthIntegration,
    HeatmapCell,
    HelpDocQuery,
    HelpDocRow,
    HelpDocsKpis,
    ImpersonationLogEntry,
    ImportKpis,
    ImportPattern,
    ImportPrompt,
    ImportRun,
    JobFailure,
    JobKpis,
    Kpi,
    KvNamespace,
    LegalDocSummary,
    LegalVersionRow,
    LicenseDispute,
    LicenseRow,
    LicenseType,
    LicensingKpis,
    NotificationFailure,
    NotificationKpis,
    PayoutStatusRow,
    PlanDistributionRow,
    PlanProfitRow,
    PlatformSplit,
    PromptRegistryRow,
    Publisher,
    PulseService,
    R2Bucket,
    RestoreKpis,
    RestoreTimelineRow,
    RevenueSummary,
    RoutingPolicy,
    SegmentCounts,
    ServiceCost,
    SessionPolicy,
    ShareKpis,
    ShareReport,
    ShareRow,
    SignupPoint,
    SoftDeleteRow,
    StorageKpis,
    SupportKpis,
    SupportTicket,
    TaxonomyKpis,
    TaxonomyStage,
    TaxonomyTag,
    TaxonomyWorkflow,
    TemplatePerformance,
    TopErrorRow,
    TopStorageUser,
    UserAuditEntry,
    UserBillingHealth,
    UserDevice,
    UserLegalAcceptance,
    WorkerRatePoint
} from './types';

const spark = (seed: number) =>
    Array.from({ length: 24 }, (_, i) =>
        Math.round(12 + 8 * Math.sin(seed + i * 0.6) + (i % 5) * 1.5)
    );

export const FIXTURE_KPIS: Kpi[] = [
    {
        id: 'users',
        label: 'Total users',
        value: '12,847',
        delta: { direction: 'up', pct: '+2.8%' },
        spark: spark(1),
        href: '/users'
    },
    {
        id: 'mrr',
        label: 'MRR',
        value: '$48,204',
        delta: { direction: 'up', pct: '+4.1%' },
        spark: spark(2),
        href: '/billing'
    },
    {
        id: 'active-tracks',
        label: 'Active tracks (30d)',
        value: '184,219',
        delta: { direction: 'up', pct: '+6.3%' },
        spark: spark(3),
        href: '/users?segment=active'
    },
    {
        id: 'queue',
        label: 'Queue depth',
        value: '1,248',
        delta: { direction: 'down', pct: '-18%' },
        spark: spark(4),
        href: '/background-jobs'
    },
    {
        id: 'r2',
        label: 'R2 storage',
        value: '42.8 TB',
        delta: { direction: 'up', pct: '+1.4%' },
        spark: spark(5),
        href: '/storage'
    },
    {
        id: 'errors',
        label: 'Errors 24h',
        value: '87',
        delta: { direction: 'up', pct: '+14%' },
        spark: spark(6),
        href: '/error-logs',
        alert: true
    },
    {
        id: 'tickets',
        label: 'Open tickets',
        value: '12',
        delta: { direction: 'flat', pct: '0%' },
        spark: spark(7),
        href: '/support'
    },
    {
        id: 'sla',
        label: 'SLA breaches',
        value: '2',
        delta: { direction: 'up', pct: '+1' },
        spark: spark(8),
        href: '/support?filter=sla_breached',
        alert: true
    },
    {
        id: 'failed-payments',
        label: 'Failed payments',
        value: '5',
        delta: { direction: 'down', pct: '-2' },
        spark: spark(9),
        href: '/billing?filter=payment_failed'
    },
    {
        id: 'unconfirmed',
        label: 'Unconfirmed >48h',
        value: '37',
        delta: { direction: 'up', pct: '+3' },
        spark: spark(10),
        href: '/users?segment=unconfirmed'
    },
    {
        id: 'abandoned',
        label: 'Abandoned onboarding',
        value: '52',
        delta: { direction: 'flat', pct: '±0' },
        spark: spark(11),
        href: '/users?segment=abandoned'
    },
    {
        id: 'pending-payouts',
        label: 'Pending payouts',
        value: '$1,284',
        delta: { direction: 'up', pct: '+$420' },
        spark: spark(12),
        href: '/licensing'
    }
];

export const FIXTURE_ALERTS: Alert[] = [
    {
        id: 'a1',
        severity: 'danger',
        message: 'Stripe webhook failure streak: 4 in the last hour',
        meta: '3 min ago',
        href: '/system-health'
    },
    {
        id: 'a2',
        severity: 'warning',
        message: 'Cron `recover-stuck-analysis` last run 38m ago (threshold 30m)',
        meta: '12 min ago',
        href: '/background-jobs'
    },
    {
        id: 'a3',
        severity: 'info',
        message: 'ToS v4.2 acceptance 88% after 14d grace — 1,542 users pending',
        meta: '1h ago',
        href: '/compliance'
    }
];

export const FIXTURE_ACTIVITY: ActivityEvent[] = [
    {
        id: 'e1',
        ts: '14:32:04',
        category: 'signup',
        actor: 'sophie.bloom@gmail.com',
        message: 'signed up',
        context: 'via web · no plan',
        href: '/users'
    },
    {
        id: 'e2',
        ts: '14:31:18',
        category: 'plan_upgrade',
        actor: 'miles@oaklab.studio',
        message: 'upgraded to',
        context: 'Studio',
        href: '/users'
    },
    {
        id: 'e3',
        ts: '14:30:02',
        category: 'share_created',
        actor: 'kelly.r@proton.me',
        message: 'created share',
        context: 'midnight_rain · 3 bounces',
        href: '/share-pages'
    },
    {
        id: 'e4',
        ts: '14:29:41',
        category: 'payment_failed',
        actor: 'rob@crestfield.co',
        message: 'payment failed',
        context: 'card expired · Pro',
        href: '/billing'
    },
    {
        id: 'e5',
        ts: '14:29:12',
        category: 'first_import',
        actor: 'nia@indie-lab.com',
        message: 'completed first import',
        context: '42 files · 8 groups',
        href: '/users'
    },
    {
        id: 'e6',
        ts: '14:27:38',
        category: 'license_sold',
        actor: 'producer@glimbr.com',
        message: 'sold license',
        context: '"rivers" · Non-excl · $199',
        href: '/licensing'
    },
    {
        id: 'e7',
        ts: '14:26:50',
        category: 'support_ticket',
        actor: 'ops@glimbr.com',
        message: 'opened ticket',
        context: '#PD-1047 · urgent',
        href: '/support'
    },
    {
        id: 'e8',
        ts: '14:24:11',
        category: 'plan_downgrade',
        actor: 'diane.v@mailbox.io',
        message: 'downgraded to',
        context: 'Free',
        href: '/users'
    },
    {
        id: 'e9',
        ts: '14:23:02',
        category: 'admin_destructive',
        actor: 'admin: ross@glimbr.com',
        message: 'suspended',
        context: 'fraud+test@mail.com · fraud',
        href: '/admin-audit'
    },
    {
        id: 'e10',
        ts: '14:22:15',
        category: 'signup',
        actor: 'paul.a@maildrop.cc',
        message: 'signed up',
        context: 'via invite · Pro trial',
        href: '/users'
    }
];

export const FIXTURE_PULSE: PulseService[] = [
    { id: 'worker-prod', name: 'Worker (prod)', status: 'up', metric: 'p95 142ms' },
    { id: 'worker-dev', name: 'Worker (dev)', status: 'up', metric: 'p95 138ms' },
    { id: 'worker-docs', name: 'Worker (docs)', status: 'up', metric: 'p95 98ms' },
    { id: 'hyperdrive', name: 'Hyperdrive / Supabase', status: 'up', metric: 'pool 38%' },
    { id: 'r2-images', name: 'R2 images bucket', status: 'up', metric: '0 errors' },
    { id: 'stripe-webhook', name: 'Stripe webhooks', status: 'degraded', metric: '4 failed' },
    { id: 'audio-telemetry', name: 'Audio analysis telemetry', status: 'up', metric: '96.2% ok' },
    { id: 'cloudflare-email', name: 'Cloudflare Email', status: 'up', metric: '99.8% delivered' },
    { id: 'glimbr-ai', name: 'Glimbr (support AI)', status: 'up', metric: 'p95 820ms' }
];

export const FIXTURE_SIGNUPS: SignupPoint[] = Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (29 - i));
    const base = 28 + Math.sin(i * 0.4) * 6 + (i > 20 ? 8 : 0);
    const count = Math.round(base + (i % 3) * 2);
    return { date: d.toISOString().slice(0, 10), count, ma7: Math.round(base) };
});

export const FIXTURE_REVENUE: RevenueSummary = {
    new_subs: 86,
    upgrades: 24,
    cancellations: 9,
    refunds: 2,
    failed: 5,
    churn_pct: 2.1
};

export const FIXTURE_TOP_ERRORS: TopErrorRow[] = [
    { code: 'STRIPE_WEBHOOK_TIMEOUT', count: 42, sample: 'POST /webhooks/stripe (524)' },
    { code: 'AUDIO_ANALYSIS_FAILED', count: 18, sample: 'Essentia decode failed: .m4a' },
    { code: 'DROPBOX_TOKEN_EXPIRED', count: 11, sample: '401 · refresh_token invalid' },
    { code: 'HYPERDRIVE_POOL_EXHAUSTED', count: 9, sample: 'no conn available after 30s' },
    { code: 'R2_UPLOAD_RETRY', count: 7, sample: '503 · temporary failure' }
];

export const FIXTURE_SEGMENT_COUNTS: SegmentCounts = {
    all: 12847,
    new: 248,
    unconfirmed: 37,
    abandoned: 52,
    active: 4218,
    inactive: 6892,
    paying: 812,
    pending_legal: 1542,
    suspended: 18,
    deleted: 14
};

function makeUser(
    id: string,
    name: string,
    email: string,
    opts: Partial<AdminUser> = {}
): AdminUser {
    return {
        id,
        email,
        name,
        avatar_hue: (parseInt(id.replace(/\D/g, '') || '1', 10) * 47) % 360,
        app_version: '1.8.2',
        platform: 'electron-mac',
        status: 'active',
        plan: 'pro',
        tracks: 0,
        storage_gb: 0,
        storage_limit_gb: 100,
        onboarding_step: 5,
        onboarding_complete: true,
        email_verified: true,
        revenue_lifetime: 0,
        cost_lifetime: 0,
        gross_profit: 0,
        last_active: '2m ago',
        signed_up: '2024-12-14',
        country: 'US',
        pending_legal: false,
        segments: ['all', 'active', 'paying'],
        ...opts,
        // derive segments from state if not explicitly passed
        ...(opts.segments ? {} : {})
    };
}

export const FIXTURE_USERS: AdminUser[] = [
    makeUser('usr_001', 'Miles Harper', 'miles@oaklab.studio', {
        plan: 'studio',
        tracks: 428,
        storage_gb: 82.4,
        revenue_lifetime: 1184,
        cost_lifetime: 212,
        gross_profit: 972,
        last_active: '2m ago',
        segments: ['all', 'active', 'paying']
    }),
    makeUser('usr_002', 'Kelly Rhodes', 'kelly.r@proton.me', {
        plan: 'pro',
        tracks: 212,
        storage_gb: 34.2,
        revenue_lifetime: 408,
        cost_lifetime: 94,
        gross_profit: 314,
        last_active: '6m ago',
        segments: ['all', 'active', 'paying']
    }),
    makeUser('usr_003', 'Nia Okafor', 'nia@indie-lab.com', {
        plan: 'trial',
        tracks: 42,
        storage_gb: 8.1,
        onboarding_step: 5,
        revenue_lifetime: 0,
        cost_lifetime: 3.8,
        gross_profit: -3.8,
        last_active: '19m ago',
        signed_up: '2026-04-14',
        segments: ['all', 'new', 'active']
    }),
    makeUser('usr_004', 'Diane Villanueva', 'diane.v@mailbox.io', {
        plan: 'free',
        status: 'inactive',
        tracks: 14,
        storage_gb: 1.2,
        revenue_lifetime: 48,
        cost_lifetime: 18,
        gross_profit: 30,
        last_active: '112d ago',
        segments: ['all', 'inactive']
    }),
    makeUser('usr_005', 'Rob Crestfield', 'rob@crestfield.co', {
        plan: 'pro',
        status: 'active',
        tracks: 96,
        storage_gb: 22.4,
        revenue_lifetime: 216,
        cost_lifetime: 48,
        gross_profit: 168,
        last_active: '38m ago',
        segments: ['all', 'active', 'paying']
    }),
    makeUser('usr_006', 'Sophie Bloom', 'sophie.bloom@gmail.com', {
        plan: 'free',
        status: 'new',
        tracks: 0,
        storage_gb: 0,
        onboarding_step: 1,
        onboarding_complete: false,
        email_verified: false,
        revenue_lifetime: 0,
        cost_lifetime: 0,
        gross_profit: 0,
        last_active: 'just now',
        signed_up: '2026-04-19',
        segments: ['all', 'new', 'unconfirmed']
    }),
    makeUser('usr_007', 'Paul Avery', 'paul.a@maildrop.cc', {
        plan: 'trial',
        status: 'new',
        tracks: 3,
        storage_gb: 0.2,
        onboarding_step: 3,
        onboarding_complete: false,
        revenue_lifetime: 0,
        cost_lifetime: 0.4,
        gross_profit: -0.4,
        last_active: '12m ago',
        signed_up: '2026-04-19',
        segments: ['all', 'new']
    }),
    makeUser('usr_008', 'Sam Larkin', 'sam.larkin@gmail.com', {
        plan: 'free',
        status: 'abandoned',
        tracks: 0,
        storage_gb: 0,
        onboarding_step: 2,
        onboarding_complete: false,
        revenue_lifetime: 0,
        cost_lifetime: 0,
        gross_profit: 0,
        last_active: '8d ago',
        signed_up: '2026-04-12',
        segments: ['all', 'abandoned']
    }),
    makeUser('usr_009', 'Bea Osei', 'bea.osei@glimbr.com', {
        plan: 'team',
        status: 'active',
        tracks: 1284,
        storage_gb: 412,
        storage_limit_gb: 500,
        revenue_lifetime: 3120,
        cost_lifetime: 622,
        gross_profit: 2498,
        last_active: '2s ago',
        segments: ['all', 'active', 'paying']
    }),
    makeUser('usr_010', 'Chris Tyler', 'chris@tyler.fm', {
        plan: 'pro',
        status: 'suspended',
        tracks: 18,
        storage_gb: 2.8,
        revenue_lifetime: 84,
        cost_lifetime: 28,
        gross_profit: 56,
        last_active: '3d ago',
        segments: ['all', 'suspended']
    }),
    makeUser('usr_011', 'Fraud Tester', 'fraud+test@mail.com', {
        plan: 'free',
        status: 'deleted',
        tracks: 0,
        storage_gb: 0,
        revenue_lifetime: 0,
        cost_lifetime: 0,
        gross_profit: 0,
        last_active: '5d ago',
        segments: ['all', 'deleted']
    }),
    makeUser('usr_012', 'Vera Holbrook', 'vera@holbrook.studio', {
        plan: 'studio',
        platform: 'electron-win',
        app_version: '1.7.9',
        tracks: 318,
        storage_gb: 56.8,
        revenue_lifetime: 1080,
        cost_lifetime: 198,
        gross_profit: 882,
        last_active: '4h ago',
        segments: ['all', 'active', 'paying']
    }),
    makeUser('usr_013', 'Jordan Pike', 'jordan.p@gmail.com', {
        plan: 'pro',
        tracks: 74,
        storage_gb: 18.1,
        revenue_lifetime: 96,
        cost_lifetime: 22,
        gross_profit: 74,
        last_active: '1h ago',
        segments: ['all', 'active', 'paying']
    }),
    makeUser('usr_014', 'Ines Marcello', 'ines@marcello.it', {
        plan: 'free',
        status: 'inactive',
        platform: 'browser',
        tracks: 4,
        storage_gb: 0.4,
        revenue_lifetime: 0,
        cost_lifetime: 0.2,
        gross_profit: -0.2,
        last_active: '96d ago',
        segments: ['all', 'inactive']
    }),
    makeUser('usr_015', 'Kenji Watanabe', 'kenji@watanabe.jp', {
        plan: 'trial',
        status: 'new',
        app_version: '1.8.2',
        tracks: 22,
        storage_gb: 4.2,
        revenue_lifetime: 0,
        cost_lifetime: 1.8,
        gross_profit: -1.8,
        last_active: '4m ago',
        signed_up: '2026-04-17',
        country: 'JP',
        segments: ['all', 'new', 'active']
    }),
    makeUser('usr_016', 'Marta Sousa', 'marta@sousa.pt', {
        plan: 'pro',
        tracks: 142,
        storage_gb: 28.6,
        revenue_lifetime: 288,
        cost_lifetime: 62,
        gross_profit: 226,
        last_active: '22m ago',
        country: 'PT',
        segments: ['all', 'active', 'paying']
    }),
    makeUser('usr_017', 'Theo Winters', 'theo@winters.au', {
        plan: 'free',
        status: 'unconfirmed',
        email_verified: false,
        platform: 'browser',
        tracks: 0,
        storage_gb: 0,
        onboarding_step: 1,
        onboarding_complete: false,
        revenue_lifetime: 0,
        cost_lifetime: 0,
        gross_profit: 0,
        last_active: '3d ago',
        signed_up: '2026-04-16',
        country: 'AU',
        segments: ['all', 'unconfirmed']
    }),
    makeUser('usr_018', 'Olivia Reeve', 'olivia.reeve@mailfence.com', {
        plan: 'studio',
        tracks: 512,
        storage_gb: 104.2,
        storage_limit_gb: 200,
        revenue_lifetime: 2196,
        cost_lifetime: 392,
        gross_profit: 1804,
        last_active: '5m ago',
        segments: ['all', 'active', 'paying']
    }),
    makeUser('usr_019', 'Nasir Iqbal', 'nasir.i@indie.co', {
        plan: 'free',
        status: 'abandoned',
        tracks: 0,
        storage_gb: 0,
        onboarding_step: 2,
        onboarding_complete: false,
        revenue_lifetime: 0,
        cost_lifetime: 0,
        gross_profit: 0,
        last_active: '6d ago',
        signed_up: '2026-04-13',
        segments: ['all', 'abandoned']
    })
];

export const FIXTURE_BILLING_HEALTH: Record<string, UserBillingHealth> = {
    usr_001: {
        stripe_sync: 'ok',
        revenue_lifetime: 1184,
        cost_breakdown: {
            ai: 28,
            supabase: 42,
            r2: 68,
            stripe: 36,
            cloudflare_email: 2,
            other: 36
        },
        gross_profit: 972,
        next_charge_date: '2026-05-14',
        next_charge_amount: 48,
        overage: { storage: false, ai: false, tracks: false }
    }
};

export const FIXTURE_LEGAL_ACCEPTANCE: UserLegalAcceptance[] = [
    { document: 'tos', version: 'v4.2', accepted_at: '2026-03-14' },
    { document: 'privacy', version: 'v3.1', accepted_at: '2026-03-14' },
    { document: 'eula', version: 'v2.0', accepted_at: '2024-12-14' },
    { document: 'cookie', version: 'v1.4', accepted_at: '2026-03-14' }
];

export const FIXTURE_AUDIT_LOG: UserAuditEntry[] = [
    {
        id: 'al1',
        ts: '14:32',
        category: 'data',
        event: 'Created track group',
        context: 'midnight_rain',
        ip: '68.12.x.x',
        app_version: '1.8.2'
    },
    {
        id: 'al2',
        ts: '14:18',
        category: 'share',
        event: 'Created share',
        context: 'playlist · 3 tracks',
        ip: '68.12.x.x'
    },
    {
        id: 'al3',
        ts: '13:02',
        category: 'billing',
        event: 'Payment method updated',
        context: 'Visa •• 4242'
    },
    {
        id: 'al4',
        ts: '12:44',
        category: 'auth',
        event: 'Signed in',
        context: 'macOS · Electron 1.8.2',
        ip: '68.12.x.x',
        app_version: '1.8.2'
    },
    {
        id: 'al5',
        ts: '10:22',
        category: 'ai',
        event: 'Chat message',
        context: '142 tokens · Sonnet'
    },
    {
        id: 'al6',
        ts: 'yday',
        category: 'data',
        event: 'Imported files',
        context: '42 files · 8 groups',
        app_version: '1.8.2'
    }
];

export const FIXTURE_DEVICES: UserDevice[] = [
    {
        id: 'd1',
        platform: 'macOS 15.2 (Apple Silicon)',
        app_version: '1.8.2',
        essentia_version: '2.1-beta6',
        algo_version: '2026-03-14',
        last_seen: '2m ago',
        update_channel: 'stable'
    },
    {
        id: 'd2',
        platform: 'macOS 15.1 (Intel)',
        app_version: '1.7.9',
        essentia_version: '2.0',
        algo_version: '2026-01-22',
        last_seen: '11d ago',
        update_channel: 'stable'
    }
];

// ============================================================
// Support fixtures
// ============================================================

export const FIXTURE_SUPPORT_KPIS: SupportKpis = {
    open: 12,
    avg_first_response: '1h 42m',
    avg_resolution: '8h 12m',
    csat: 4.6,
    ai_fcr_rate: 42.8,
    breaching_sla: 2,
    unassigned: 3,
    backlog_over_7d: 1
};

export const FIXTURE_TICKETS: SupportTicket[] = [
    {
        id: 't1',
        number: 2518,
        status: 'open',
        priority: 'urgent',
        subject: "Studio plan payment failed — can't access stems",
        genesis_channel: 'email',
        active_channels: ['email'],
        customer_email: 'miles@oaklab.studio',
        customer_name: 'Miles Harper',
        user_id: 'usr_001',
        topic_tags: ['billing', 'access'],
        sla_breaches: true,
        sla_countdown: '−12m',
        age: '2h',
        last_message: '"My card was replaced yesterday, the webhook seems stuck."',
        ai_handled: false
    },
    {
        id: 't2',
        number: 2517,
        status: 'in_progress',
        priority: 'high',
        subject: 'BPM wrong for half-tempo tracks',
        genesis_channel: 'help_ai',
        active_channels: ['help_ai', 'email'],
        customer_email: 'nia@indie-lab.com',
        customer_name: 'Nia Okafor',
        user_id: 'usr_003',
        assignee: 'ross',
        topic_tags: ['bpm-wrong', 'analysis'],
        sla_breaches: false,
        sla_countdown: '2h 48m',
        age: '42m',
        last_message: 'Glimbr triaged, escalated to human after 2 turns.',
        ai_handled: true
    },
    {
        id: 't3',
        number: 2516,
        status: 'waiting_customer',
        priority: 'normal',
        subject: 'How do I connect Dropbox?',
        genesis_channel: 'chat',
        active_channels: ['chat'],
        customer_email: 'sophie.bloom@gmail.com',
        customer_name: 'Sophie Bloom',
        user_id: 'usr_006',
        topic_tags: ['onboarding', 'dropbox'],
        sla_breaches: false,
        sla_countdown: 'paused',
        age: '6h',
        last_message: 'Sent 3 help links — waiting on reply.',
        ai_handled: true
    },
    {
        id: 't4',
        number: 2515,
        status: 'open',
        priority: 'urgent',
        subject: 'Refund request — accidental Studio upgrade',
        genesis_channel: 'email',
        active_channels: ['email'],
        customer_email: 'rob@crestfield.co',
        customer_name: 'Rob Crestfield',
        user_id: 'usr_005',
        topic_tags: ['billing', 'refund'],
        sla_breaches: false,
        sla_countdown: '38m',
        age: '22m',
        last_message: 'Gated topic — assigned to owner (Ross).',
        ai_handled: false
    },
    {
        id: 't5',
        number: 2514,
        status: 'in_progress',
        priority: 'normal',
        subject: 'Share link returning 404',
        genesis_channel: 'email',
        active_channels: ['email', 'chat'],
        customer_email: 'kelly.r@proton.me',
        customer_name: 'Kelly Rhodes',
        user_id: 'usr_002',
        assignee: 'support',
        topic_tags: ['share-404'],
        sla_breaches: false,
        sla_countdown: '18h',
        age: '3h',
        last_message: 'Admin replied, waiting on follow-up.',
        ai_handled: false
    },
    {
        id: 't6',
        number: 2513,
        status: 'resolved',
        priority: 'low',
        subject: 'Feature request: dark mode toggle in onboarding',
        genesis_channel: 'chat',
        active_channels: ['chat'],
        customer_email: 'vera@holbrook.studio',
        customer_name: 'Vera Holbrook',
        user_id: 'usr_012',
        topic_tags: ['feature-request'],
        sla_breaches: false,
        sla_countdown: '—',
        age: '1d',
        last_message: 'Logged + closed, CSAT 5.',
        ai_handled: true
    }
];

export const FIXTURE_TICKET_MESSAGES: Record<string, TicketMessage[]> = {
    t1: [
        {
            id: 'm1-1',
            ts: '2026-04-19 12:18',
            channel: 'email',
            direction: 'inbound',
            author_type: 'customer',
            author_name: 'Miles Harper',
            body_text:
                "My card was replaced yesterday — the webhook seems stuck. I can't access the Studio stems and my deal is due Friday. Any way to unblock?"
        },
        {
            id: 'm1-2',
            ts: '2026-04-19 12:19',
            channel: 'email',
            direction: 'outbound',
            author_type: 'glimbr_ai',
            author_name: 'Glimbr (auto-reply)',
            body_text:
                "Thanks for getting in touch Miles — we've opened ticket #PD-2518 and a human will review your payment issue. Estimated response: 1 hour (urgent).",
            ai_confidence: 0.92,
            ai_tokens: 84
        },
        {
            id: 'm1-3',
            ts: '2026-04-19 12:22',
            channel: 'email',
            direction: 'internal_note',
            author_type: 'admin',
            author_name: 'Aisha Patel',
            body_text:
                'Checked Stripe — card.updated webhook 524d at 12:14 UTC. Going to force a plan resync before replying.'
        }
    ],
    t2: [
        {
            id: 'm2-1',
            ts: '2026-04-19 13:50',
            channel: 'help_ai',
            direction: 'inbound',
            author_type: 'customer',
            author_name: 'Nia Okafor',
            body_text:
                'The BPM on 3 of my tracks is exactly half of what it should be — 70 vs 140. Help?'
        },
        {
            id: 'm2-2',
            ts: '2026-04-19 13:50',
            channel: 'help_ai',
            direction: 'outbound',
            author_type: 'glimbr_ai',
            author_name: 'Glimbr',
            body_text:
                'Half-tempo detection is a known class of error. I can trigger a re-analysis on those tracks with a corrected tempo hint. Want me to do that?',
            ai_confidence: 0.88,
            ai_tokens: 118
        },
        {
            id: 'm2-3',
            ts: '2026-04-19 13:52',
            channel: 'help_ai',
            direction: 'inbound',
            author_type: 'customer',
            author_name: 'Nia Okafor',
            body_text: 'Sure, go ahead — but also I want a human to double-check the algo.'
        },
        {
            id: 'm2-4',
            ts: '2026-04-19 13:53',
            channel: 'email',
            direction: 'outbound',
            author_type: 'glimbr_ai',
            author_name: 'Glimbr',
            body_text:
                'Escalating to a human after your request. Ross will follow up within 4 hours.',
            ai_confidence: 0.95,
            ai_tokens: 42
        }
    ]
};

export const FIXTURE_CANNED_REPLIES: CannedReply[] = [
    {
        slug: 'dropbox-reconnect',
        title: 'Dropbox reconnect walkthrough',
        body: 'Hi {{first_name}},\n\nThanks for getting in touch. Dropbox tokens expire after 4 hours of inactivity — to reconnect:\n1. Open Settings → Integrations\n2. Click "Reconnect Dropbox"\n3. Sign in and authorise\n\nIf you still see the issue after that, reply and I\'ll pull your logs.\n\n— Producer Dashboard support'
    },
    {
        slug: 'bpm-half-tempo',
        title: 'BPM half-tempo explanation',
        body: "Hi {{first_name}},\n\nHalf-tempo detection is a common error when a track has a prominent backbeat. I've requeued your tracks with a corrected tempo hint — you should see updated BPM within a few minutes.\n\n— Producer Dashboard support"
    },
    {
        slug: 'share-404',
        title: 'Share link 404 fix',
        body: "Hi {{first_name}},\n\nA share link returns 404 usually because the share was revoked or expired. Check the owner's dashboard under Share pages → Active shares. If the share exists and still 404s, send me the URL and I'll investigate.\n\n— Producer Dashboard support"
    }
];

export const FIXTURE_HEALTH_INTEGRATIONS: HealthIntegration[] = [
    {
        id: 'worker-prod',
        name: 'Worker (prod)',
        endpoint: 'client.producerdashboard.app',
        status: 'healthy',
        critical: true,
        req_per_min: 1248,
        p95_ms: 142,
        error_pct: 0.08,
        sparkline: spark(1)
    },
    {
        id: 'worker-dev',
        name: 'Worker (dev)',
        endpoint: 'producer-dashboard.workers.dev',
        status: 'healthy',
        critical: false,
        req_per_min: 42,
        p95_ms: 138,
        error_pct: 0.02,
        sparkline: spark(2)
    },
    {
        id: 'worker-docs',
        name: 'Worker (docs)',
        endpoint: 'docs.producerdashboard.app',
        status: 'healthy',
        critical: true,
        req_per_min: 184,
        p95_ms: 98,
        error_pct: 0.0,
        sparkline: spark(3)
    },
    {
        id: 'hyperdrive',
        name: 'Hyperdrive / Supabase',
        status: 'healthy',
        critical: true,
        req_per_min: 3218,
        p95_ms: 42,
        error_pct: 0.01,
        sparkline: spark(4)
    },
    {
        id: 'stripe',
        name: 'Stripe webhooks',
        status: 'degraded',
        critical: true,
        req_per_min: 14,
        p95_ms: 2420,
        error_pct: 28.5,
        sparkline: spark(5)
    },
    {
        id: 'r2',
        name: 'R2 storage',
        status: 'healthy',
        critical: true,
        req_per_min: 942,
        p95_ms: 88,
        error_pct: 0.01,
        sparkline: spark(6)
    },
    {
        id: 'anthropic',
        name: 'Anthropic API',
        status: 'healthy',
        critical: true,
        req_per_min: 212,
        p95_ms: 820,
        error_pct: 0.08,
        sparkline: spark(7)
    },
    {
        id: 'openai',
        name: 'OpenAI API',
        status: 'elevated',
        critical: true,
        req_per_min: 88,
        p95_ms: 1210,
        error_pct: 0.42,
        sparkline: spark(8)
    },
    {
        id: 'cloudflare-email',
        name: 'Cloudflare Email',
        status: 'healthy',
        critical: true,
        req_per_min: 142,
        p95_ms: 320,
        error_pct: 0.12,
        sparkline: spark(9)
    },
    {
        id: 'twilio',
        name: 'Twilio',
        status: 'healthy',
        critical: false,
        req_per_min: 18,
        p95_ms: 480,
        error_pct: 0.0,
        sparkline: spark(10)
    },
    {
        id: 'dropbox',
        name: 'Dropbox API',
        status: 'healthy',
        critical: true,
        req_per_min: 312,
        p95_ms: 412,
        error_pct: 0.14,
        sparkline: spark(11)
    },
    {
        id: 'github',
        name: 'GitHub Releases',
        status: 'healthy',
        critical: false,
        req_per_min: 42,
        p95_ms: 618,
        error_pct: 0.02,
        sparkline: spark(12)
    }
];

export const FIXTURE_DB_POOL: DbPoolDetail = {
    available: false,
    message: 'Hyperdrive pool occupancy is not surfaced in fixture mode.',
    probe_latency_ms: 18.2,
    active: null,
    max: null,
    idle: null,
    waiting: null,
    avg_query_ms: 18.2,
    slow_queries_24h: null,
    deadlocks_24h: null
};

export const FIXTURE_WORKER_RATE: WorkerRatePoint[] = Array.from({ length: 48 }, (_, i) => ({
    ts: `${String(Math.floor(i / 2)).padStart(2, '0')}:${i % 2 ? '30' : '00'}`,
    prod: Math.round(1000 + Math.sin(i * 0.3) * 240 + (i % 4) * 80),
    dev: Math.round(60 + Math.sin(i * 0.2) * 20),
    docs: Math.round(160 + Math.sin(i * 0.25) * 40)
}));

// ============================================================
// Background jobs
// ============================================================

export const FIXTURE_JOB_KPIS: JobKpis = {
    active: 8,
    runs_24h: 412,
    failures_24h: 2,
    stuck: 0
};

export const FIXTURE_JOBS: BackgroundJob[] = [
    {
        name: 'analysis-watchdog',
        schedule: '*/5 * * * *',
        purpose: 'Detect stuck local analyses',
        last_run: '2m ago',
        duration_ms: 412,
        success_rate_24h: 100,
        status: 'idle',
        next_run: 'in 3m'
    },
    {
        name: 'recover-stuck-analysis',
        schedule: '0 */2 * * *',
        purpose: 'Safety net: requeue tracks stuck > 2h',
        last_run: '38m ago',
        duration_ms: 2180,
        success_rate_24h: 100,
        status: 'idle',
        next_run: 'in 1h 22m'
    },
    {
        name: 'refresh-tokens',
        schedule: '0 */1 * * *',
        purpose: 'Rotate Dropbox OAuth tokens before expiry',
        last_run: '18m ago',
        duration_ms: 3820,
        success_rate_24h: 96.2,
        status: 'idle',
        next_run: 'in 42m'
    },
    {
        name: 'process-data-exports',
        schedule: '*/15 * * * *',
        purpose: 'Build GDPR data export bundles to R2',
        last_run: '8m ago',
        duration_ms: 14200,
        success_rate_24h: 100,
        status: 'idle',
        next_run: 'in 7m'
    },
    {
        name: 'process-collaboration-imports',
        schedule: '*/10 * * * *',
        purpose: 'Import tracks from accepted collaborator invites',
        last_run: '4m ago',
        duration_ms: 1890,
        success_rate_24h: 100,
        status: 'idle',
        next_run: 'in 6m'
    },
    {
        name: 'cleanup-artwork',
        schedule: '0 3 * * *',
        purpose: 'Purge R2 artwork for deleted track groups',
        last_run: '11h ago',
        duration_ms: 42100,
        success_rate_24h: 100,
        status: 'idle',
        next_run: 'in 13h'
    },
    {
        name: 'cleanup-orphaned-images',
        schedule: '0 4 * * 0',
        purpose: 'Delete R2 images with no owning track (weekly)',
        last_run: '3d ago',
        duration_ms: 18200,
        success_rate_24h: 100,
        status: 'idle',
        next_run: 'Sunday 04:00'
    },
    {
        name: 'check-share-acceptance',
        schedule: '0 */6 * * *',
        purpose: "Remind share recipients who haven't accepted",
        last_run: '2h ago',
        duration_ms: 812,
        success_rate_24h: 100,
        status: 'idle',
        next_run: 'in 4h'
    }
];

export const FIXTURE_JOB_FAILURES: JobFailure[] = [
    {
        id: 'jf1',
        job: 'refresh-tokens',
        ts: '12:18',
        severity: 'warning',
        error: 'Dropbox 429 rate-limited on batch · partial success 42/44'
    },
    {
        id: 'jf2',
        job: 'refresh-tokens',
        ts: '09:18',
        severity: 'warning',
        error: 'Dropbox 401 · 2 users with revoked tokens'
    }
];

// ============================================================
// Error logs
// ============================================================

export const FIXTURE_ERROR_LOGS: ErrorLogRow[] = [
    {
        id: 'e1',
        ts: '14:32:04',
        severity: 'error',
        service: 'Worker (prod)',
        code: 'STRIPE_WEBHOOK_TIMEOUT',
        message: 'POST /webhooks/stripe → 524 Cloudflare timeout',
        count: 14,
        trace_id: 'cf_req_9f4a12b8'
    },
    {
        id: 'e2',
        ts: '14:31:52',
        severity: 'warn',
        service: 'Worker (prod)',
        code: 'DROPBOX_TOKEN_EXPIRED',
        message: '401 · refresh_token invalid',
        user_email: 'kelly.r@proton.me',
        count: 3,
        trace_id: 'cf_req_8e2c41d9'
    },
    {
        id: 'e3',
        ts: '14:30:14',
        severity: 'error',
        service: 'Audio consumer',
        code: 'AUDIO_ANALYSIS_FAILED',
        message: 'Essentia decode failed: .m4a unsupported subformat',
        user_email: 'ines@marcello.it',
        count: 1,
        trace_id: 'cf_req_7d1f82ca'
    },
    {
        id: 'e4',
        ts: '14:28:42',
        severity: 'warn',
        service: 'Worker (prod)',
        code: 'HYPERDRIVE_POOL_EXHAUSTED',
        message: 'no conn available after 30s · queued 3',
        count: 2,
        trace_id: 'cf_req_6c092e1a'
    },
    {
        id: 'e5',
        ts: '14:26:31',
        severity: 'error',
        service: 'Worker (prod)',
        code: 'R2_UPLOAD_RETRY',
        message: '503 · temporary failure, retried 3×',
        user_email: 'miles@oaklab.studio',
        count: 1,
        trace_id: 'cf_req_5b81c472'
    },
    {
        id: 'e6',
        ts: '14:21:18',
        severity: 'fatal',
        service: 'Worker (prod)',
        code: 'UNHANDLED_REJECTION',
        message: 'TypeError: cannot read property "plan" of null',
        count: 1,
        trace_id: 'cf_req_4a2d8901'
    },
    {
        id: 'e7',
        ts: '14:18:02',
        severity: 'warn',
        service: 'Frontend',
        code: 'SENTRY_JS',
        message: 'ChunkLoadError · loading vendor-c8e1b.js failed',
        count: 8,
        trace_id: 'sentry_42e9a8c2'
    }
];

// ============================================================
// Billing
// ============================================================

export const FIXTURE_BILLING_KPIS: BillingKpis = {
    mrr: {
        id: 'mrr',
        label: 'MRR',
        value: '$48,204',
        delta: { direction: 'up', pct: '+4.1%' },
        spark: spark(20),
        href: '#'
    },
    arr: {
        id: 'arr',
        label: 'ARR',
        value: '$578,448',
        delta: { direction: 'up', pct: '+4.1%' },
        spark: spark(21),
        href: '#'
    },
    paying: {
        id: 'paying',
        label: 'Paying users',
        value: '812',
        delta: { direction: 'up', pct: '+18' },
        spark: spark(22),
        href: '/users?segment=paying'
    },
    churn: {
        id: 'churn',
        label: 'Churn (30d)',
        value: '2.1%',
        delta: { direction: 'down', pct: '−0.4pp' },
        spark: spark(23),
        href: '#'
    },
    ltv: {
        id: 'ltv',
        label: 'LTV',
        value: '$2,814',
        delta: { direction: 'up', pct: '+3.8%' },
        spark: spark(24),
        href: '#'
    },
    failed: {
        id: 'failed',
        label: 'Failed payments',
        value: '5',
        delta: { direction: 'down', pct: '−2' },
        spark: spark(25),
        href: '#',
        alert: true
    },
    revenue_30d: {
        id: 'rev-30d',
        label: 'Revenue (30d)',
        value: '$52,180',
        delta: { direction: 'up', pct: '+6.2%' },
        spark: spark(26),
        href: '#'
    },
    cost_30d: {
        id: 'cost-30d',
        label: 'Service costs (30d)',
        value: '$12,440',
        delta: { direction: 'up', pct: '+2.8%' },
        spark: spark(27),
        href: '#'
    },
    profit_30d: {
        id: 'profit-30d',
        label: 'Gross profit (30d)',
        value: '$39,740',
        delta: { direction: 'up', pct: '+7.3%' },
        spark: spark(28),
        href: '#'
    },
    margin: {
        id: 'margin',
        label: 'Gross margin',
        value: '76.2%',
        delta: { direction: 'up', pct: '+0.8pp' },
        spark: spark(29),
        href: '#'
    },
    arpu: {
        id: 'arpu',
        label: 'ARPU (paying)',
        value: '$64',
        delta: { direction: 'flat', pct: '±0' },
        spark: spark(30),
        href: '#'
    },
    cost_per_free: {
        id: 'cost-per-free',
        label: 'Cost per Free',
        value: '$0.42',
        delta: { direction: 'up', pct: '+$0.06' },
        spark: spark(31),
        href: '#'
    }
};

export const FIXTURE_PLAN_PROFIT: PlanProfitRow[] = [
    {
        plan: 'Free',
        users: 8942,
        revenue_per_user: 0,
        cost_per_user: 0.42,
        profit_per_user: -0.42,
        margin_pct: null
    },
    {
        plan: 'Trial',
        users: 312,
        revenue_per_user: 0,
        cost_per_user: 1.84,
        profit_per_user: -1.84,
        margin_pct: null
    },
    {
        plan: 'Pro',
        users: 428,
        revenue_per_user: 19,
        cost_per_user: 4.8,
        profit_per_user: 14.2,
        margin_pct: 74.7
    },
    {
        plan: 'Studio',
        users: 268,
        revenue_per_user: 49,
        cost_per_user: 10.6,
        profit_per_user: 38.4,
        margin_pct: 78.4
    },
    {
        plan: 'Team',
        users: 116,
        revenue_per_user: 149,
        cost_per_user: 24.2,
        profit_per_user: 124.8,
        margin_pct: 83.8
    }
];

export const FIXTURE_SERVICE_COSTS: ServiceCost[] = [
    { provider: 'Stripe fees', amount: 2212, pct: 17.8 },
    {
        provider: 'Anthropic + OpenAI',
        amount: 3420,
        pct: 27.5,
        note: 'chat + import review + classifier'
    },
    { provider: 'Cloudflare Workers', amount: 1180, pct: 9.5 },
    { provider: 'Supabase', amount: 2840, pct: 22.8, note: 'DB + auth + realtime + storage' },
    { provider: 'Cloudflare R2', amount: 1420, pct: 11.4 },
    { provider: 'Cloudflare Email', amount: 320, pct: 2.6 },
    { provider: 'MiniMax / OpenRouter', amount: 412, pct: 3.3, note: 'help-doc regen' },
    { provider: 'GitHub Releases CDN', amount: 412, pct: 3.3 },
    { provider: 'Sentry + misc', amount: 224, pct: 1.8 }
];

export const FIXTURE_PLAN_DISTRIBUTION: PlanDistributionRow[] = [
    { plan: 'Free', users: 8942, pct: 69.6, profit_total: -3755 },
    { plan: 'Trial', users: 312, pct: 2.4, profit_total: -574 },
    { plan: 'Pro', users: 428, pct: 3.3, profit_total: 6078 },
    { plan: 'Studio', users: 268, pct: 2.1, profit_total: 10291 },
    { plan: 'Team', users: 116, pct: 0.9, profit_total: 14477 },
    { plan: 'Other', users: 2781, pct: 21.7, profit_total: 0 }
];

export const FIXTURE_BILLING_TRANSACTIONS: BillingTransaction[] = [
    {
        id: 'tx1',
        ts: '14:32',
        kind: 'success',
        amount: 49,
        user: 'miles@oaklab.studio',
        note: 'Studio renewal'
    },
    {
        id: 'tx2',
        ts: '14:28',
        kind: 'upgrade',
        amount: 149,
        user: 'bea.osei@glimbr.com',
        note: 'Pro → Team'
    },
    {
        id: 'tx3',
        ts: '14:20',
        kind: 'failure',
        amount: 19,
        user: 'rob@crestfield.co',
        note: 'card_declined · will retry'
    },
    {
        id: 'tx4',
        ts: '13:54',
        kind: 'success',
        amount: 19,
        user: 'kelly.r@proton.me',
        note: 'Pro renewal'
    },
    {
        id: 'tx5',
        ts: '13:42',
        kind: 'refund',
        amount: -49,
        user: 'diane.v@mailbox.io',
        note: 'within 14 days'
    },
    { id: 'tx6', ts: '13:30', kind: 'success', amount: 49, user: 'olivia.reeve@mailfence.com' },
    {
        id: 'tx7',
        ts: '13:12',
        kind: 'downgrade',
        amount: 0,
        user: 'vera@holbrook.studio',
        note: 'Studio → Pro'
    },
    { id: 'tx8', ts: '12:48', kind: 'success', amount: 19, user: 'marta@sousa.pt' }
];

export const FIXTURE_BILLING_HEALTH_QUEUE: BillingHealthRow[] = [
    {
        id: 'bh1',
        user_email: 'rob@crestfield.co',
        user_name: 'Rob Crestfield',
        plan: 'pro',
        issue: 'payment_failed',
        detail: 'Card expired · retries exhausted',
        exposure: 228,
        detected: '22m ago'
    },
    {
        id: 'bh2',
        user_email: 'miles@oaklab.studio',
        user_name: 'Miles Harper',
        plan: 'studio',
        issue: 'plan_drift',
        detail: 'Stripe says studio · DB says pro',
        exposure: 588,
        detected: '1h ago'
    },
    {
        id: 'bh3',
        user_email: 'bea.osei@glimbr.com',
        user_name: 'Bea Osei',
        plan: 'team',
        issue: 'overage',
        detail: 'storage · 412 GB of 500 GB (82%)',
        exposure: 0,
        detected: '4h ago'
    },
    {
        id: 'bh4',
        user_email: 'chris@tyler.fm',
        user_name: 'Chris Tyler',
        plan: 'pro',
        issue: 'card_expiring',
        detail: 'Visa •• 4242 expires in 18 days',
        exposure: 228,
        detected: '2d ago'
    },
    {
        id: 'bh5',
        user_email: 'sam.larkin@gmail.com',
        user_name: 'Sam Larkin',
        plan: 'trial',
        issue: 'trial_ending',
        detail: 'No payment method on file · 2 days left',
        exposure: 19,
        detected: '6h ago'
    },
    {
        id: 'bh6',
        user_email: 'fraud+test@mail.com',
        user_name: 'Fraud Tester',
        plan: 'pro',
        issue: 'dispute',
        detail: 'Chargeback: fraudulent',
        exposure: 228,
        detected: '12h ago'
    },
    {
        id: 'bh7',
        user_email: 'olivia.reeve@mailfence.com',
        user_name: 'Olivia Reeve',
        plan: 'studio',
        issue: 'annual_renewal',
        detail: 'Renewal in 21 days · $588',
        exposure: 588,
        detected: '1d ago'
    }
];

// ============================================================
// Licensing
// ============================================================

export const FIXTURE_LICENSING_KPIS: LicensingKpis = {
    licenses_30d: 42,
    revenue_30d: 8420,
    pd_cut_30d: 1010,
    pending_payouts_count: 12,
    pending_payouts_total: 1284,
    active_disputes: 2,
    connect_accounts: 38,
    avg_deal: 201
};

export const FIXTURE_LICENSE_TYPES: LicenseType[] = [
    { id: 'lt1', name: 'Standard non-exclusive', count: 28, avg_deal: 149, active: true },
    { id: 'lt2', name: 'Premium non-exclusive', count: 9, avg_deal: 299, active: true },
    { id: 'lt3', name: 'Exclusive', count: 3, avg_deal: 1200, active: true },
    { id: 'lt4', name: 'Stem pack', count: 2, avg_deal: 80, active: true },
    { id: 'lt5', name: 'Sync license', count: 0, avg_deal: 0, active: false }
];

export const FIXTURE_LICENSES: LicenseRow[] = [
    {
        id: 'l1',
        track: 'rivers',
        licensor: 'producer@glimbr.com',
        licensee: 'sync.studio.co',
        type: 'Standard',
        amount: 199,
        status: 'delivered',
        splits: '100 / 0',
        ts: '14:28'
    },
    {
        id: 'l2',
        track: 'midnight_rain',
        licensor: 'kelly.r@proton.me',
        licensee: 'adhouse.nyc',
        type: 'Premium',
        amount: 299,
        status: 'awaiting_stems',
        splits: '60 / 40',
        ts: '12:14'
    },
    {
        id: 'l3',
        track: 'afterglow',
        licensor: 'miles@oaklab.studio',
        licensee: 'indie-films.co',
        type: 'Exclusive',
        amount: 1500,
        status: 'awaiting_payment',
        splits: '80 / 20',
        ts: 'yday'
    },
    {
        id: 'l4',
        track: 'golden_hour',
        licensor: 'bea.osei@glimbr.com',
        licensee: 'coffee.co',
        type: 'Standard',
        amount: 149,
        status: 'disputed',
        splits: '100 / 0',
        ts: '2d ago'
    },
    {
        id: 'l5',
        track: 'paperlight',
        licensor: 'vera@holbrook.studio',
        licensee: 'brandco.net',
        type: 'Stem pack',
        amount: 80,
        status: 'refunded',
        splits: '100 / 0',
        ts: '3d ago'
    }
];

export const FIXTURE_PAYOUT_STATUS: PayoutStatusRow[] = [
    { label: 'Paid (30d)', count: 24, amount: 6420, tone: 'success' },
    { label: 'Pending — next run', count: 12, amount: 1284, tone: 'info' },
    { label: 'Held (W-9 required)', count: 3, amount: 420, tone: 'warning' },
    { label: 'Failed', count: 1, amount: 149, tone: 'danger' },
    { label: 'Connect KYC pending', count: 2, tone: 'muted' }
];

export const FIXTURE_DISPUTES: LicenseDispute[] = [
    {
        id: 'd1',
        severity: 'danger',
        title: 'Sample clearance claim · "golden_hour"',
        age: '2d',
        state: 'open',
        type: 'sample_clearance'
    },
    {
        id: 'd2',
        severity: 'warning',
        title: 'Refund request · accidental Exclusive listing',
        age: '18h',
        state: 'in_review',
        type: 'refund_request'
    }
];

export const FIXTURE_PUBLISHERS: Publisher[] = [
    { id: 'p1', name: 'Oaklab Studio', share_pct: 60, tracks: 28, lifetime_revenue: 1844 },
    { id: 'p2', name: 'Glimbr Publishing', share_pct: 100, tracks: 42, lifetime_revenue: 3120 },
    { id: 'p3', name: 'Proton Music Co', share_pct: 50, tracks: 18, lifetime_revenue: 612 }
];

// ============================================================
// Analytics
// ============================================================

export const FIXTURE_ANALYTICS_KPIS: AnalyticsKpis = {
    dau: 2840,
    wau: 7120,
    mau: 12847,
    stickiness: 22.1,
    avg_session_min: 32,
    sessions_per_user: 1.8,
    power_users: 412,
    dormant: 1284
};

export const FIXTURE_DAU: DauPoint[] = Array.from({ length: 30 }, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (29 - i));
    const base = 2600 + Math.sin(i * 0.3) * 180 + (i > 20 ? 200 : 0);
    return {
        date: d.toISOString().slice(0, 10),
        dau: Math.round(base + (i % 3) * 80),
        stickiness: Math.round(20 + Math.sin(i * 0.4) * 2 + (i > 20 ? 1 : 0))
    };
});

export const FIXTURE_PLATFORM_SPLIT: PlatformSplit[] = [
    { os: 'macOS (Apple Silicon)', count: 4212, pct: 32.8 },
    { os: 'macOS (Intel)', count: 1840, pct: 14.3 },
    { os: 'Windows 11', count: 3184, pct: 24.8 },
    { os: 'Windows 10', count: 1212, pct: 9.4 },
    { os: 'Chrome (browser)', count: 1820, pct: 14.2 },
    { os: 'Safari (browser)', count: 412, pct: 3.2 },
    { os: 'Other', count: 167, pct: 1.3 }
];

export const FIXTURE_HEATMAP: HeatmapCell[] = Array.from({ length: 7 * 24 }, (_, i) => {
    const day = Math.floor(i / 24);
    const hour = i % 24;
    const weekend = day >= 5;
    const evening = hour >= 17 && hour <= 22;
    const morning = hour >= 8 && hour <= 11;
    const base = weekend ? 0.6 : 1.0;
    let sessions = base * (20 + Math.sin(hour * 0.3) * 10);
    if (evening) sessions *= 2.2;
    if (morning) sessions *= 1.6;
    if (hour < 6 || hour > 23) sessions *= 0.2;
    return { day, hour, sessions: Math.round(sessions) };
});

export const FIXTURE_FEATURE_ADOPTION: FeatureAdoption[] = [
    { feature: 'Track grid', adoption_pct: 98.2, mau_count: 12616 },
    { feature: 'Playback', adoption_pct: 92.4, mau_count: 11869 },
    { feature: 'Tagging', adoption_pct: 68.2, mau_count: 8761 },
    { feature: 'Bucket / project', adoption_pct: 48.6, mau_count: 6244 },
    { feature: 'Share link', adoption_pct: 34.2, mau_count: 4394 },
    { feature: 'AI chat', adoption_pct: 28.4, mau_count: 3648 },
    { feature: 'Collaborators', adoption_pct: 22.1, mau_count: 2839 },
    { feature: 'Comments', adoption_pct: 16.4, mau_count: 2107 },
    { feature: 'Focus mode', adoption_pct: 12.8, mau_count: 1644 },
    { feature: 'Licensing listings', adoption_pct: 4.2, mau_count: 540 },
    { feature: 'BYOK', adoption_pct: 3.1, mau_count: 398 },
    { feature: 'Public profile', adoption_pct: 2.4, mau_count: 308 }
];

export const FIXTURE_GEO: GeoRow[] = [
    { country: 'United States', mau: 4218, revenue: 28410 },
    { country: 'United Kingdom', mau: 1412, revenue: 6820 },
    { country: 'Germany', mau: 942, revenue: 3820 },
    { country: 'Canada', mau: 812, revenue: 3120 },
    { country: 'Australia', mau: 612, revenue: 2840 },
    { country: 'Netherlands', mau: 418, revenue: 1680 },
    { country: 'France', mau: 384, revenue: 1420 },
    { country: 'Japan', mau: 312, revenue: 1284 },
    { country: 'Sweden', mau: 282, revenue: 1080 },
    { country: 'Brazil', mau: 248, revenue: 920 }
];

export const FIXTURE_EVENTS: AnalyticsEvent[] = [
    {
        id: 'ev1',
        ts: '14:32:04',
        event_type: 'track.import',
        user_email: 'nia@indie-lab.com',
        properties: { files: '42' }
    },
    {
        id: 'ev2',
        ts: '14:31:58',
        event_type: 'share.create',
        user_email: 'kelly.r@proton.me',
        properties: { type: 'playlist' }
    },
    { id: 'ev3', ts: '14:31:42', event_type: 'auth.signin', user_email: 'miles@oaklab.studio' },
    {
        id: 'ev4',
        ts: '14:31:18',
        event_type: 'chat.message',
        user_email: 'rob@crestfield.co',
        properties: { tokens: '142' }
    },
    {
        id: 'ev5',
        ts: '14:30:58',
        event_type: 'billing.subscription_created',
        user_email: 'sophie.bloom@gmail.com',
        properties: { plan: 'pro' }
    },
    { id: 'ev6', ts: '14:30:42', event_type: 'tag.apply', user_email: 'vera@holbrook.studio' },
    { id: 'ev7', ts: '14:29:58', event_type: 'focus.override', user_email: 'bea.osei@glimbr.com' },
    { id: 'ev8', ts: '14:29:20', event_type: 'comment.create', user_email: 'chris@tyler.fm' },
    {
        id: 'ev9',
        ts: '14:28:42',
        event_type: 'license.sold',
        user_email: 'producer@glimbr.com',
        properties: { amount: '199' }
    },
    {
        id: 'ev10',
        ts: '14:28:18',
        event_type: 'onboarding.complete',
        user_email: 'paul.a@maildrop.cc'
    }
];

export const FIXTURE_FUNNEL_ONBOARDING: FunnelStep[] = [
    { step: 'Signed up', count: 4218, pct: 100, dropoff: 0 },
    { step: 'Email verified', count: 3842, pct: 91.1, dropoff: 376 },
    { step: 'Genre & role picked', count: 3218, pct: 76.3, dropoff: 624 },
    { step: 'Dropbox connected', count: 2484, pct: 58.9, dropoff: 734 },
    { step: 'First import kicked off', count: 2218, pct: 52.6, dropoff: 266 },
    { step: 'First import completed', count: 2108, pct: 50.0, dropoff: 110 },
    { step: 'Onboarding completed', count: 1984, pct: 47.0, dropoff: 124 }
];

export const FIXTURE_FUNNEL_ACTIVATION: FunnelStep[] = [
    { step: 'Signed up', count: 4218, pct: 100, dropoff: 0 },
    { step: 'First track imported', count: 2108, pct: 50.0, dropoff: 2110 },
    { step: 'First tag applied', count: 1418, pct: 33.6, dropoff: 690 },
    { step: 'First share created', count: 812, pct: 19.2, dropoff: 606 },
    { step: 'First collaborator added', count: 412, pct: 9.8, dropoff: 400 },
    { step: 'Hit free plan limit', count: 284, pct: 6.7, dropoff: 128 },
    { step: 'Started checkout', count: 186, pct: 4.4, dropoff: 98 },
    { step: 'Converted to paid', count: 124, pct: 2.9, dropoff: 62 }
];

export const FIXTURE_COHORTS: CohortRow[] = [
    {
        cohort: '2026-W16',
        size: 412,
        retention: { W0: 100, W1: 58, W2: 42, W4: 34, W8: null, W12: null, W24: null }
    },
    {
        cohort: '2026-W15',
        size: 482,
        retention: { W0: 100, W1: 62, W2: 45, W4: 36, W8: 28, W12: null, W24: null }
    },
    {
        cohort: '2026-W14',
        size: 438,
        retention: { W0: 100, W1: 60, W2: 44, W4: 35, W8: 28, W12: null, W24: null }
    },
    {
        cohort: '2026-W13',
        size: 512,
        retention: { W0: 100, W1: 64, W2: 46, W4: 38, W8: 30, W12: 24, W24: null }
    },
    {
        cohort: '2026-W12',
        size: 428,
        retention: { W0: 100, W1: 58, W2: 42, W4: 34, W8: 27, W12: 22, W24: null }
    },
    {
        cohort: '2026-W11',
        size: 482,
        retention: { W0: 100, W1: 63, W2: 44, W4: 36, W8: 28, W12: 24, W24: null }
    },
    {
        cohort: '2026-W10',
        size: 398,
        retention: { W0: 100, W1: 56, W2: 40, W4: 31, W8: 26, W12: 21, W24: 18 }
    },
    {
        cohort: '2026-W09',
        size: 452,
        retention: { W0: 100, W1: 60, W2: 43, W4: 34, W8: 28, W12: 22, W24: 18 }
    }
];

// ============================================================
// Compliance
// ============================================================

export const FIXTURE_COMPLIANCE_KPIS: ComplianceKpis = {
    current_tos: 'v4.2',
    current_tos_since: '2026-03-14',
    current_privacy: 'v3.1',
    current_privacy_since: '2026-03-14',
    on_latest_pct: 88.0,
    pending_reaccept: 1542,
    idle_expired_30d: 284,
    violations: 0,
    active_policies: 4
};

export const FIXTURE_LEGAL_DOCS: LegalDocSummary[] = [
    {
        slug: 'tos',
        title: 'Terms of Service',
        current_version: 'v4.2',
        effective_at: '2026-03-14',
        summary_of_change:
            'Added licensing marketplace clauses and AI-generated content attribution',
        acceptance_pct: 88.0,
        enforcement_mode: 'modal_with_grace'
    },
    {
        slug: 'privacy',
        title: 'Privacy Policy',
        current_version: 'v3.1',
        effective_at: '2026-03-14',
        summary_of_change: 'Clarified analytics opt-out and added BYOK key handling notice',
        acceptance_pct: 96.2,
        enforcement_mode: 'banner_only'
    },
    {
        slug: 'eula',
        title: 'End-User License Agreement',
        current_version: 'v2.0',
        effective_at: '2024-12-14',
        summary_of_change: 'Electron desktop app licensing terms',
        acceptance_pct: 99.8,
        enforcement_mode: 'strict_signin_block'
    },
    {
        slug: 'cookie',
        title: 'Cookie Policy',
        current_version: 'v1.4',
        effective_at: '2026-03-14',
        summary_of_change: 'Replaced third-party analytics with first-party CF tracking',
        acceptance_pct: 98.4,
        enforcement_mode: 'banner_only'
    }
];

export const FIXTURE_LEGAL_VERSIONS: LegalVersionRow[] = [
    {
        id: 'v1',
        slug: 'tos',
        version: 'v4.2',
        published_at: '2026-03-14',
        published_by: 'ross@glimbr.com',
        enforcement_mode: 'modal_with_grace',
        summary_of_change: 'Licensing + AI attribution',
        acceptance_pct: 88.0
    },
    {
        id: 'v2',
        slug: 'tos',
        version: 'v4.1',
        published_at: '2025-11-02',
        published_by: 'ross@glimbr.com',
        enforcement_mode: 'banner_only',
        summary_of_change: 'Minor editorial fixes',
        acceptance_pct: 99.4
    },
    {
        id: 'v3',
        slug: 'tos',
        version: 'v4.0',
        published_at: '2025-06-14',
        published_by: 'ross@glimbr.com',
        enforcement_mode: 'strict_signin_block',
        summary_of_change: 'Major rewrite for subscription model',
        acceptance_pct: 100.0
    }
];

export const FIXTURE_SESSION_POLICY: SessionPolicy = {
    idle_timeout_user_minutes: 30,
    idle_timeout_admin_minutes: 15,
    max_session_lifetime_days: 7,
    require_2fa_for_admins: true,
    reauth_on_password_change: true,
    reauth_on_new_device: true,
    reauth_on_tos_update: true,
    reauth_every_days: 30,
    idle_expired_30d: 284,
    lifetime_expired_30d: 1842,
    active_sessions: 2840
};

export const FIXTURE_ACCEPTANCE_LOG: AcceptanceLogEntry[] = [
    {
        id: 'al1',
        user_email: 'miles@oaklab.studio',
        document: 'tos',
        version: 'v4.2',
        accepted_at: '2026-04-18 14:12',
        ip: '68.12.x.x',
        trigger: 'forced_reaccept'
    },
    {
        id: 'al2',
        user_email: 'kelly.r@proton.me',
        document: 'tos',
        version: 'v4.2',
        accepted_at: '2026-04-18 13:48',
        ip: '92.40.x.x',
        trigger: 'forced_reaccept'
    },
    {
        id: 'al3',
        user_email: 'nia@indie-lab.com',
        document: 'tos',
        version: 'v4.2',
        accepted_at: '2026-04-18 11:22',
        ip: '104.8.x.x',
        trigger: 'signup'
    },
    {
        id: 'al4',
        user_email: 'sophie.bloom@gmail.com',
        document: 'privacy',
        version: 'v3.1',
        accepted_at: '2026-04-18 10:04',
        ip: '72.18.x.x',
        trigger: 'banner'
    },
    {
        id: 'al5',
        user_email: 'rob@crestfield.co',
        document: 'tos',
        version: 'v4.2',
        accepted_at: '2026-04-17 18:42',
        ip: '24.92.x.x',
        trigger: 'forced_reaccept'
    }
];

// ============================================================
// Feature Flags
// ============================================================

export const FIXTURE_FLAG_KPIS: FeatureFlagKpis = {
    active: 12,
    rolling_out: 4,
    with_overrides: 3,
    changed_this_week: 6
};

export const FIXTURE_FLAGS: FeatureFlagRow[] = [
    {
        slug: 'ai_chat_v2',
        description: 'Next-gen Glimbr chat UX with thread branching',
        status: 'rolling_out',
        rollout_pct: 40,
        overrides_count: 2,
        owner: 'ross',
        updated_at: '2h ago',
        target_plans: ['pro', 'studio', 'team']
    },
    {
        slug: 'licensing_marketplace_v1',
        description: 'Public marketplace storefront + Stripe Connect flows',
        status: 'rolling_out',
        rollout_pct: 10,
        overrides_count: 5,
        owner: 'ross',
        updated_at: '1d ago'
    },
    {
        slug: 'focus_mode_haptics',
        description: 'Trackpad haptic feedback on section transitions',
        status: 'internal',
        rollout_pct: 0,
        overrides_count: 3,
        owner: 'ross',
        updated_at: '3d ago'
    },
    {
        slug: 'byok_openai_gpt_5_4',
        description: 'Allow GPT-5.4 BYOK key submissions',
        status: 'on',
        rollout_pct: 100,
        overrides_count: 0,
        owner: 'ross',
        updated_at: '7d ago'
    },
    {
        slug: 'public_profile_beta',
        description: 'User-facing profile page at /u/<handle>',
        status: 'rolling_out',
        rollout_pct: 25,
        overrides_count: 1,
        owner: 'ross',
        updated_at: '4d ago',
        target_plans: ['pro', 'studio', 'team']
    },
    {
        slug: 'split_sheets_docusign',
        description: 'DocuSign integration for split-sheet signatures',
        status: 'draft',
        rollout_pct: 0,
        overrides_count: 0,
        owner: 'ross',
        updated_at: '11d ago'
    },
    {
        slug: 'waveform_v2_engine',
        description: 'New waveform rendering engine (2x faster)',
        status: 'on',
        rollout_pct: 100,
        overrides_count: 0,
        owner: 'ross',
        updated_at: '22d ago'
    },
    {
        slug: 'old_import_v1',
        description: 'Pre-JW-grouping import pipeline',
        status: 'removed',
        rollout_pct: 0,
        overrides_count: 0,
        owner: 'ross',
        updated_at: '90d ago'
    }
];

// ============================================================
// AI & BYOK
// ============================================================

export const FIXTURE_AI_KPIS: AiKpis = {
    byok_users: 398,
    chat_30d: 18420,
    cost_30d: 3420,
    frontier_bypass_pct: 18.2
};

export const FIXTURE_AI_MODEL_USAGE: AiModelUsage[] = [
    { tier: 'classifier', model: 'Claude Haiku 4.5', messages_30d: 42180, cost_30d: 320 },
    { tier: 'executor', model: 'Claude Sonnet 4.6', messages_30d: 18420, cost_30d: 1840 },
    { tier: 'frontier', model: 'Claude Opus 4.7 + GPT-5.4', messages_30d: 3218, cost_30d: 1080 },
    { tier: 'byok', model: '(user keys)', messages_30d: 4820, cost_30d: 180 }
];

export const FIXTURE_BYOK_HEALTH: ByokHealth = {
    valid: 382,
    rate_limited_24h: 12,
    invalid: 4,
    master_key_age_days: 42,
    master_key_rotation_days: 90
};

export const FIXTURE_ROUTING_POLICY: RoutingPolicy = {
    classifier_model: 'Claude Haiku 4.5',
    executor_model: 'Claude Sonnet 4.6',
    frontier_eligible_plans: ['studio', 'team'],
    fallback: 'Downgrade to Sonnet on classifier failure'
};

export const FIXTURE_AI_TOP_USERS: AiTopUser[] = [
    {
        id: 'usr_001',
        email: 'miles@oaklab.studio',
        plan: 'studio',
        cost_30d: 84,
        messages_30d: 1820
    },
    { id: 'usr_009', email: 'bea.osei@glimbr.com', plan: 'team', cost_30d: 72, messages_30d: 1680 },
    {
        id: 'usr_018',
        email: 'olivia.reeve@mailfence.com',
        plan: 'studio',
        cost_30d: 58,
        messages_30d: 1240
    },
    { id: 'usr_002', email: 'kelly.r@proton.me', plan: 'pro', cost_30d: 42, messages_30d: 820 },
    { id: 'usr_016', email: 'marta@sousa.pt', plan: 'pro', cost_30d: 36, messages_30d: 720 }
];

export const FIXTURE_PROMPT_REGISTRY: PromptRegistryRow[] = [
    { slug: 'chat.system', feature: 'AI chat', model: 'Sonnet 4.6', updated_at: '2026-04-12' },
    {
        slug: 'import.review',
        feature: 'Import pipeline',
        model: 'Sonnet 4.6',
        updated_at: '2026-04-02'
    },
    {
        slug: 'import.pattern_discovery',
        feature: 'Import pipeline',
        model: 'Haiku 4.5',
        updated_at: '2026-03-28'
    },
    {
        slug: 'import.display_name_cleaner',
        feature: 'Import pipeline',
        model: 'Haiku 4.5',
        updated_at: '2026-03-14'
    },
    {
        slug: 'support.glimbr_system',
        feature: 'Support AI',
        model: 'Sonnet 4.6',
        updated_at: '2026-04-18'
    },
    {
        slug: 'help_docs.regen',
        feature: 'Help docs',
        model: 'MiniMax abab6',
        updated_at: '2026-04-08'
    }
];

// ============================================================
// Taxonomy
// ============================================================

export const FIXTURE_TAXONOMY_KPIS: TaxonomyKpis = {
    stages: 8,
    workflows_system: 3,
    workflows_optional: 2,
    standard_tags: 58,
    custom_tags: 4218
};

export const FIXTURE_STAGES: TaxonomyStage[] = [
    {
        id: 's1',
        name: 'Idea',
        color_var: '--purple',
        sort_order: 1,
        track_count: 1820,
        active: true
    },
    { id: 's2', name: 'Demo', color_var: '--cyan', sort_order: 2, track_count: 3418, active: true },
    {
        id: 's3',
        name: 'Arrangement',
        color_var: '--primary',
        sort_order: 3,
        track_count: 1240,
        active: true
    },
    {
        id: 's4',
        name: 'Tracking',
        color_var: '--warning',
        sort_order: 4,
        track_count: 812,
        active: true
    },
    {
        id: 's5',
        name: 'Mix',
        color_var: '--success',
        sort_order: 5,
        track_count: 482,
        active: true
    },
    {
        id: 's6',
        name: 'Master',
        color_var: '--danger',
        sort_order: 6,
        track_count: 248,
        active: true
    },
    {
        id: 's7',
        name: 'Release',
        color_var: '--text-muted',
        sort_order: 7,
        track_count: 184,
        active: true
    },
    {
        id: 's8',
        name: 'Archived',
        color_var: '--text-dim',
        sort_order: 8,
        track_count: 1288,
        active: true
    }
];

export const FIXTURE_WORKFLOWS: TaxonomyWorkflow[] = [
    {
        id: 'wf1',
        name: 'Linear',
        description: 'Idea → Demo → Arrangement → Tracking → Mix → Master → Release',
        is_system: true,
        stage_sequence: ['s1', 's2', 's3', 's4', 's5', 's6', 's7'],
        active: true
    },
    {
        id: 'wf2',
        name: 'Electronic',
        description: 'Idea → Demo → Arrangement → Mix → Master → Release',
        is_system: true,
        stage_sequence: ['s1', 's2', 's3', 's5', 's6', 's7'],
        active: true
    },
    {
        id: 'wf3',
        name: 'Sync-first',
        description: 'Idea → Demo → Mix → Master (optimised for brief turnaround)',
        is_system: true,
        stage_sequence: ['s1', 's2', 's5', 's6'],
        active: true
    },
    {
        id: 'wf4',
        name: 'Band',
        description: 'Idea → Rehearsal → Tracking → Mix → Master → Release',
        is_system: false,
        stage_sequence: ['s1', 's2', 's4', 's5', 's6', 's7'],
        active: true
    }
];

export const FIXTURE_TAGS: TaxonomyTag[] = [
    {
        id: 'tg1',
        slug: 'pop',
        name: 'Pop',
        category: 'genre',
        color_var: '--primary',
        active: true
    },
    { id: 'tg2', slug: 'rnb', name: 'R&B', category: 'genre', color_var: '--purple', active: true },
    {
        id: 'tg3',
        slug: 'hiphop',
        name: 'Hip-hop',
        category: 'genre',
        color_var: '--cyan',
        active: true
    },
    {
        id: 'tg4',
        slug: 'indie',
        name: 'Indie',
        category: 'genre',
        color_var: '--success',
        active: true
    },
    {
        id: 'tg5',
        slug: 'electronic',
        name: 'Electronic',
        category: 'genre',
        color_var: '--warning',
        active: true
    },
    {
        id: 'tg6',
        slug: 'chill',
        name: 'Chill',
        category: 'mood',
        color_var: '--cyan',
        active: true
    },
    {
        id: 'tg7',
        slug: 'uplifting',
        name: 'Uplifting',
        category: 'mood',
        color_var: '--success',
        active: true
    },
    {
        id: 'tg8',
        slug: 'dark',
        name: 'Dark',
        category: 'mood',
        color_var: '--purple',
        active: true
    },
    {
        id: 'tg9',
        slug: 'energetic',
        name: 'Energetic',
        category: 'mood',
        color_var: '--danger',
        active: true
    },
    {
        id: 'tg10',
        slug: 'drums',
        name: 'Drums',
        category: 'instrumentation',
        color_var: '--primary',
        active: true
    },
    {
        id: 'tg11',
        slug: 'vocals',
        name: 'Vocals',
        category: 'instrumentation',
        color_var: '--warning',
        active: true
    },
    {
        id: 'tg12',
        slug: 'bass',
        name: 'Bass',
        category: 'instrumentation',
        color_var: '--success',
        active: true
    },
    {
        id: 'tg13',
        slug: 'sync',
        name: 'Sync',
        category: 'purpose',
        color_var: '--purple',
        active: true
    },
    {
        id: 'tg14',
        slug: 'release',
        name: 'Release',
        category: 'purpose',
        color_var: '--primary',
        active: true
    },
    {
        id: 'tg15',
        slug: 'wip',
        name: 'WIP',
        category: 'status',
        color_var: '--warning',
        active: true
    },
    {
        id: 'tg16',
        slug: 'final',
        name: 'Final',
        category: 'status',
        color_var: '--success',
        active: true
    },
    { id: 'tg17', slug: 'wav', name: 'WAV', category: 'format', color_var: '--cyan', active: true },
    { id: 'tg18', slug: 'mp3', name: 'MP3', category: 'format', color_var: '--muted', active: true }
];

// ============================================================
// Share pages
// ============================================================

export const FIXTURE_SHARE_KPIS: ShareKpis = {
    active: 842,
    views_30d: 18420,
    comments_30d: 312,
    abuse_open: 3,
    abuse_resolved: 28,
    abuse_pending: 1
};

export const FIXTURE_SHARES: ShareRow[] = [
    {
        token: 'sh_abc123',
        title: 'midnight_rain · final mix',
        type: 'track',
        owner: 'kelly.r@proton.me',
        permissions: { bounces: true, stems: false, projects: false },
        password: false,
        views: 142,
        comments: 8,
        created_at: '2d ago',
        expires_at: '12d',
        status: 'active'
    },
    {
        token: 'sh_def456',
        title: 'rivers · playlist',
        type: 'playlist',
        owner: 'miles@oaklab.studio',
        permissions: { bounces: true, stems: true, projects: false },
        password: true,
        views: 42,
        comments: 3,
        created_at: '4d ago',
        expires_at: '26d',
        status: 'active'
    },
    {
        token: 'sh_ghi789',
        title: 'afterglow · demo',
        type: 'track',
        owner: 'bea.osei@glimbr.com',
        permissions: { bounces: true, stems: false, projects: false },
        password: false,
        views: 812,
        comments: 22,
        created_at: '12d ago',
        status: 'flagged'
    },
    {
        token: 'sh_jkl012',
        title: 'golden_hour',
        type: 'track',
        owner: 'vera@holbrook.studio',
        permissions: { bounces: true, stems: true, projects: true },
        password: false,
        views: 6,
        comments: 0,
        created_at: '28d ago',
        expires_at: '−2d',
        status: 'expired'
    },
    {
        token: 'sh_mno345',
        title: 'paperlight',
        type: 'track',
        owner: 'fraud+test@mail.com',
        permissions: { bounces: true, stems: false, projects: false },
        password: false,
        views: 42,
        comments: 2,
        created_at: '5d ago',
        status: 'revoked'
    }
];

export const FIXTURE_SHARE_REPORTS: ShareReport[] = [
    {
        id: 'sr1',
        share_title: 'afterglow · demo',
        reporter: 'rights@sonymusic.com',
        reason: 'copyright',
        reported_at: '14h ago',
        state: 'open'
    },
    {
        id: 'sr2',
        share_title: 'paperlight',
        reporter: 'anon',
        reason: 'inappropriate',
        reported_at: '2d ago',
        state: 'open'
    },
    {
        id: 'sr3',
        share_title: 'untitled_leak',
        reporter: 'legal@labelco.com',
        reason: 'sample_clearance',
        reported_at: '4d ago',
        state: 'open'
    }
];

// ============================================================
// Help docs
// ============================================================

export const FIXTURE_HELP_DOCS_KPIS: HelpDocsKpis = {
    total: 122,
    last_regen_at: '2026-04-17 08:42',
    searches_30d: 4820,
    most_viewed: 'Connecting Dropbox',
    failed_searches_pct: 8.2,
    satisfaction_pct: 87.4
};

export const FIXTURE_HELP_DOCS: HelpDocRow[] = [
    {
        slug: 'connect-dropbox',
        title: 'Connecting Dropbox',
        last_regenerated: '2d ago',
        views_30d: 1842,
        thumbs_up_pct: 94.2,
        stale: false
    },
    {
        slug: 'first-import',
        title: 'Your first import',
        last_regenerated: '2d ago',
        views_30d: 1428,
        thumbs_up_pct: 91.4,
        stale: false
    },
    {
        slug: 'bpm-key-wrong',
        title: 'Why is my BPM / key wrong?',
        last_regenerated: '18d ago',
        views_30d: 812,
        thumbs_up_pct: 68.4,
        stale: true,
        stale_reason: 'Regenerated > 14d ago and algo_version changed'
    },
    {
        slug: 'share-links',
        title: 'Share links explained',
        last_regenerated: '2d ago',
        views_30d: 612,
        thumbs_up_pct: 88.2,
        stale: false
    },
    {
        slug: 'buckets-projects',
        title: 'Buckets vs Projects',
        last_regenerated: '2d ago',
        views_30d: 488,
        thumbs_up_pct: 92.8,
        stale: false
    },
    {
        slug: 'collaborators',
        title: 'Inviting collaborators',
        last_regenerated: '42d ago',
        views_30d: 312,
        thumbs_up_pct: 72.4,
        stale: true,
        stale_reason: 'References removed `legacy_invite_flow`'
    },
    {
        slug: 'byok-keys',
        title: 'Bring your own AI key',
        last_regenerated: '2d ago',
        views_30d: 184,
        thumbs_up_pct: 89.4,
        stale: false
    }
];

export const FIXTURE_TOP_QUERIES: HelpDocQuery[] = [
    { query: 'dropbox not connecting', count: 312 },
    { query: 'bpm wrong', count: 218 },
    { query: 'share link 404', count: 142 },
    { query: 'delete account', count: 98 },
    { query: 'cancel subscription', count: 84 },
    { query: 'export all tracks', count: 72 },
    { query: 'key wrong half step', count: 58 }
];

export const FIXTURE_FAILED_QUERIES: HelpDocQuery[] = [
    { query: 'logic pro integration', count: 42 },
    { query: 'ableton link support', count: 28 },
    { query: 'chord detection', count: 18 },
    { query: 'stem separation', count: 12 }
];

// ============================================================
// Changelog
// ============================================================

export const FIXTURE_CHANGELOG_KPIS: ChangelogKpis = {
    published: 28,
    last_version: 'v2.5.0',
    last_at: '4d ago',
    email_subscribers: 8420,
    avg_open_rate: 48.2
};

export const FIXTURE_CHANGELOG: ChangelogEntry[] = [
    {
        id: 'cl1',
        version: 'v2.5.0-draft',
        title: 'Licensing marketplace beta',
        status: 'draft',
        categories: ['feat', 'breaking'],
        body_preview: 'Ship the public licensing marketplace with Stripe Connect payouts…',
        audience: 'paying',
        channels: ['email', 'in_app_banner', 'public_page']
    },
    {
        id: 'cl2',
        version: 'v2.5.0',
        title: 'Focus mode + BYOK for GPT-5.4',
        status: 'published',
        categories: ['feat'],
        body_preview:
            'Focus mode lets you disappear into a single track. BYOK now supports GPT-5.4…',
        published_at: '2026-04-15',
        audience: 'all',
        channels: ['email', 'in_app_banner', 'public_page'],
        email_sent_count: 8420,
        email_open_rate: 52.4,
        email_click_rate: 8.2
    },
    {
        id: 'cl3',
        version: 'v2.4.1',
        title: 'Import pipeline performance + Dropbox retry fixes',
        status: 'published',
        categories: ['fix', 'refactor'],
        body_preview: 'Imports are 40% faster on long sessions; Dropbox 401s now auto-recover…',
        published_at: '2026-04-08',
        audience: 'active',
        channels: ['email', 'public_page'],
        email_sent_count: 7120,
        email_open_rate: 46.8,
        email_click_rate: 6.4
    },
    {
        id: 'cl4',
        version: 'v2.4.0',
        title: 'Collaborators + comments',
        status: 'published',
        categories: ['feat'],
        body_preview: 'Invite collaborators on specific buckets; leave inline track comments…',
        published_at: '2026-03-28',
        audience: 'all',
        channels: ['email', 'in_app_banner', 'public_page'],
        email_sent_count: 8280,
        email_open_rate: 54.2,
        email_click_rate: 9.6
    },
    {
        id: 'cl5',
        version: 'v2.3.0-scheduled',
        title: 'Scheduled April maintenance',
        status: 'scheduled',
        categories: ['chore'],
        body_preview: 'Short Supabase maintenance window this Sunday 02:00 UTC…',
        scheduled_at: '2026-04-21 02:00',
        audience: 'all',
        channels: ['email', 'in_app_banner']
    }
];

// ============================================================
// Import pipeline
// ============================================================

export const FIXTURE_IMPORT_KPIS: ImportKpis = {
    imports_24h: 248,
    success_rate: 94.8,
    ai_reviews_24h: 184,
    ai_cost_24h: 12.42,
    pattern_hit_pct: 72.4
};

export const FIXTURE_IMPORT_RUNS: ImportRun[] = [
    {
        id: 'ir1',
        user: 'miles@oaklab.studio',
        files: 42,
        groups: 8,
        pattern: 'Ableton v3.8',
        ai_outcome: 'used',
        duration_ms: 18400,
        status: 'success',
        ts: '14:32'
    },
    {
        id: 'ir2',
        user: 'nia@indie-lab.com',
        files: 82,
        groups: 12,
        pattern: 'Logic v2.2',
        ai_outcome: 'used',
        duration_ms: 28400,
        status: 'success',
        ts: '14:18'
    },
    {
        id: 'ir3',
        user: 'kelly.r@proton.me',
        files: 18,
        groups: 4,
        pattern: 'Custom (learned)',
        ai_outcome: 'skipped',
        duration_ms: 6200,
        status: 'success',
        ts: '14:02'
    },
    {
        id: 'ir4',
        user: 'bea.osei@glimbr.com',
        files: 412,
        groups: 84,
        pattern: 'Pro Tools v1.4',
        ai_outcome: 'used',
        duration_ms: 98400,
        status: 'partial',
        ts: '13:48'
    },
    {
        id: 'ir5',
        user: 'rob@crestfield.co',
        files: 14,
        groups: 0,
        pattern: 'Unmatched',
        ai_outcome: 'timeout',
        duration_ms: 30000,
        status: 'failed',
        ts: '13:32'
    }
];

export const FIXTURE_IMPORT_PROMPTS: ImportPrompt[] = [
    {
        slug: 'review',
        title: 'Import review',
        model: 'Claude Sonnet 4.6',
        temperature: 0.2,
        max_tokens: 1200,
        active_version: 'v7',
        avg_tokens: 840,
        cost_per_import: 0.018,
        body: 'You are reviewing a proposed grouping of audio files into track groups. The naming pattern detected is {{pattern}}. Groups:\n{{groups}}\n\nFor each group, evaluate: are these files the same song? Any misgroupings? Return JSON with corrections.'
    },
    {
        slug: 'pattern-discovery',
        title: 'Pattern discovery',
        model: 'Claude Haiku 4.5',
        temperature: 0.1,
        max_tokens: 800,
        active_version: 'v4',
        avg_tokens: 420,
        cost_per_import: 0.004,
        body: 'Analyse these filenames for a consistent naming pattern:\n{{filenames}}\n\nReturn a JSON pattern spec with delimiters, version tokens, and confidence.'
    },
    {
        slug: 'display-name-cleaner',
        title: 'Display-name cleaner',
        model: 'Claude Haiku 4.5',
        temperature: 0.1,
        max_tokens: 400,
        active_version: 'v3',
        avg_tokens: 180,
        cost_per_import: 0.001,
        body: 'Given this raw filename: {{filename}}, return a clean, title-cased display name with underscores and technical suffixes removed.'
    }
];

export const FIXTURE_IMPORT_PATTERNS: ImportPattern[] = [
    { id: 'p1', name: 'Ableton v3.8', users: 842, example: 'song_name 3 Project/song_name_v3.wav' },
    { id: 'p2', name: 'Logic v2.2', users: 612, example: 'song_name_v2/bounces/song_name.wav' },
    { id: 'p3', name: 'Pro Tools v1.4', users: 284, example: 'SongName_v1.4_Bounce.wav' },
    { id: 'p4', name: 'FL Studio default', users: 182, example: 'song_name_FL_MASTER.wav' },
    { id: 'p5', name: 'Reaper', users: 82, example: 'song_name-001.wav' }
];

// ============================================================
// Storage
// ============================================================

export const FIXTURE_STORAGE_KPIS: StorageKpis = {
    total_tb: 42.8,
    monthly_cost_projection: 642,
    kv_keys: 184218,
    dropbox_tokens: 2840,
    egress_30d_tb: 3.2
};

export const FIXTURE_R2_BUCKETS: R2Bucket[] = [
    {
        name: 'pd-images',
        size_gb: 1840,
        object_count: 412082,
        free_tier_remaining_pct: 0,
        monthly_cost_projection: 28
    },
    {
        name: 'pd-waveforms',
        size_gb: 8420,
        object_count: 284218,
        free_tier_remaining_pct: 0,
        monthly_cost_projection: 126
    },
    {
        name: 'pd-audio',
        size_gb: 28420,
        object_count: 982084,
        free_tier_remaining_pct: 0,
        monthly_cost_projection: 426
    },
    {
        name: 'pd-exports',
        size_gb: 180,
        object_count: 4218,
        free_tier_remaining_pct: 0,
        monthly_cost_projection: 3
    },
    {
        name: 'pd-recordings',
        size_gb: 218,
        object_count: 282,
        free_tier_remaining_pct: 0,
        monthly_cost_projection: 3
    },
    {
        name: 'pd-backup',
        size_gb: 3680,
        object_count: 184,
        free_tier_remaining_pct: 0,
        monthly_cost_projection: 56
    }
];

export const FIXTURE_KV_NAMESPACES: KvNamespace[] = [
    {
        name: 'user-cache',
        purpose: 'User session + profile cache',
        size_mb: 82.4,
        key_count: 12842,
        reads_per_min: 8420
    },
    {
        name: 'rate-limits',
        purpose: 'Rate-limit buckets per user/ip',
        size_mb: 42.2,
        key_count: 84218,
        reads_per_min: 12418
    },
    {
        name: 'flags',
        purpose: 'Feature flag resolutions cache',
        size_mb: 4.2,
        key_count: 12,
        reads_per_min: 18420
    },
    {
        name: 'dropbox-state',
        purpose: 'OAuth state nonces',
        size_mb: 0.2,
        key_count: 82,
        reads_per_min: 4
    },
    {
        name: 'shares',
        purpose: 'Public share lookup cache',
        size_mb: 18.4,
        key_count: 842,
        reads_per_min: 1240
    }
];

export const FIXTURE_DROPBOX_TOKEN_HEALTH: DropboxTokenHealth = {
    valid: 2818,
    expiring_24h: 18,
    expired: 4,
    auto_refresh_success_pct: 99.1
};

export const FIXTURE_TOP_STORAGE_USERS: TopStorageUser[] = [
    {
        id: 'usr_018',
        email: 'olivia.reeve@mailfence.com',
        storage_gb: 104.2,
        limit_gb: 200,
        overage: false
    },
    { id: 'usr_009', email: 'bea.osei@glimbr.com', storage_gb: 412, limit_gb: 500, overage: false },
    {
        id: 'usr_001',
        email: 'miles@oaklab.studio',
        storage_gb: 82.4,
        limit_gb: 100,
        overage: false
    },
    {
        id: 'usr_012',
        email: 'vera@holbrook.studio',
        storage_gb: 56.8,
        limit_gb: 100,
        overage: false
    },
    { id: 'usr_002', email: 'kelly.r@proton.me', storage_gb: 34.2, limit_gb: 50, overage: false },
    { id: 'usr_017', email: 'theo@winters.au', storage_gb: 58.4, limit_gb: 50, overage: true }
];

// ============================================================
// Audio
// ============================================================

export const FIXTURE_AUDIO_KPIS: AudioKpis = {
    analysis_coverage_pct: 96.2,
    bpm_pct: 94.8,
    key_pct: 92.4,
    waveform_pct: 98.8,
    failures_7d: 42,
    avg_local_analysis_ms: 4820
};

export const FIXTURE_ELECTRON_VERSIONS: ElectronVersionBucket[] = [
    {
        bucket: 'latest',
        electron_version: '1.8.2',
        essentia_version: '2.1-beta6',
        users: 7820,
        pct: 60.9
    },
    {
        bucket: 'current',
        electron_version: '1.8.1',
        essentia_version: '2.1-beta5',
        users: 3218,
        pct: 25.0
    },
    { bucket: 'stale', electron_version: '1.7.9', essentia_version: '2.0', users: 1640, pct: 12.8 },
    {
        bucket: 'unsupported',
        electron_version: '< 1.7',
        essentia_version: 'legacy',
        users: 169,
        pct: 1.3
    }
];

export const FIXTURE_AUDIO_FORMATS: AudioFormatRow[] = [
    { format: 'WAV', count_30d: 184200, pct: 54.2 },
    { format: 'MP3', count_30d: 82400, pct: 24.3 },
    { format: 'FLAC', count_30d: 42180, pct: 12.4 },
    { format: 'AIFF', count_30d: 18200, pct: 5.4 },
    { format: 'M4A', count_30d: 8400, pct: 2.5 },
    { format: 'OGG', count_30d: 4200, pct: 1.2 }
];

export const FIXTURE_AUDIO_ACCURACY: AudioAccuracy = {
    bpm_within_2: 92.4,
    bpm_half_double_err: 4.8,
    key_correct: 86.2,
    key_relative_minor_err: 8.4,
    user_corrections_pct: 6.2,
    opt_in_pct: 74.8
};

export const FIXTURE_AUDIO_FAILURES: AudioFailureRow[] = [
    {
        id: 'af1',
        track: 'midnight_rain.m4a',
        owner: 'ines@marcello.it',
        electron_version: '1.8.2',
        essentia_version: '2.1-beta6',
        reason: 'decode_error · unsupported subformat',
        retries: 3,
        ts: '14:28'
    },
    {
        id: 'af2',
        track: 'afterglow_stems_L.wav',
        owner: 'bea.osei@glimbr.com',
        electron_version: '1.7.9',
        essentia_version: '2.0',
        reason: 'wasm_oom',
        retries: 2,
        ts: '13:42'
    },
    {
        id: 'af3',
        track: 'paperlight.aif',
        owner: 'chris@tyler.fm',
        electron_version: '1.8.2',
        essentia_version: '2.1-beta6',
        reason: 'corrupt_header',
        retries: 3,
        ts: '12:18'
    }
];

export const FIXTURE_AUDIO_CAMPAIGNS: AudioCampaign[] = [
    {
        id: 'ac1',
        name: 'Algo v4 rollout',
        target_algo_version: 'v4',
        scope: 'tracks on algo ≤ v3',
        progress_pct: 68,
        tracks_processed: 18420,
        tracks_total: 27100,
        status: 'active'
    },
    {
        id: 'ac2',
        name: 'Essentia 2.1 re-analysis',
        target_algo_version: 'v3.2',
        scope: 'all tracks on essentia 2.0',
        progress_pct: 100,
        tracks_processed: 184218,
        tracks_total: 184218,
        status: 'completed'
    }
];

// ============================================================
// Notifications
// ============================================================

export const FIXTURE_NOTIFICATION_KPIS: NotificationKpis = {
    sent_24h: 8420,
    success_pct: 99.1,
    email_open_rate: 52.4,
    bounces_24h: 18,
    bounces_hard: 4,
    bounces_soft: 14,
    unsubscribes_30d: 48,
    followups_queued: 12
};

export const FIXTURE_CHANNEL_DELIVERY: ChannelDelivery[] = [
    { channel: 'Email', provider: 'Cloudflare Email', sent_24h: 6820, success_pct: 99.2 },
    { channel: 'In-app toast', provider: 'Supabase Realtime', sent_24h: 842, success_pct: 100 },
    { channel: 'In-app inbox', provider: 'DB', sent_24h: 312, success_pct: 100 },
    {
        channel: 'Electron native',
        provider: 'OS notification centre',
        sent_24h: 418,
        success_pct: 98.6
    },
    { channel: 'Web push', provider: 'VAPID / SW', sent_24h: 14, success_pct: 100 },
    { channel: 'SMS', provider: 'Twilio', sent_24h: 14, success_pct: 100 }
];

export const FIXTURE_TEMPLATES: TemplatePerformance[] = [
    { slug: 'welcome', category: 'Onboarding', sent_30d: 4218, open_rate: 68.4, click_rate: 32.4 },
    {
        slug: 'email_verification',
        category: 'Onboarding',
        sent_30d: 4218,
        open_rate: 78.2,
        click_rate: 58.4
    },
    {
        slug: 'password_reset',
        category: 'Security',
        sent_30d: 612,
        open_rate: 84.2,
        click_rate: 72.1
    },
    {
        slug: 'collaborator_invite',
        category: 'Collaboration',
        sent_30d: 1842,
        open_rate: 72.4,
        click_rate: 48.2
    },
    { slug: 'trial_ending', category: 'Billing', sent_30d: 284, open_rate: 62.4, click_rate: 28.4 },
    {
        slug: 'subscription_activated',
        category: 'Billing',
        sent_30d: 186,
        open_rate: 54.2,
        click_rate: 12.4
    },
    {
        slug: 'payment_failed',
        category: 'Billing',
        sent_30d: 42,
        open_rate: 88.2,
        click_rate: 68.4
    },
    { slug: 'limit_reached', category: 'Billing', sent_30d: 18, open_rate: 78.2, click_rate: 48.4 },
    {
        slug: 'release_notes',
        category: 'Marketing',
        sent_30d: 8420,
        open_rate: 52.4,
        click_rate: 8.2
    },
    {
        slug: 'legal_update',
        category: 'Compliance',
        sent_30d: 12842,
        open_rate: 68.4,
        click_rate: 42.1
    },
    { slug: 'payout_sent', category: 'Licensing', sent_30d: 24, open_rate: 92.4, click_rate: 18.4 }
];

export const FIXTURE_NOTIFICATION_FAILURES: NotificationFailure[] = [
    {
        id: 'nf1',
        ts: '14:18',
        channel: 'Email',
        template: 'welcome',
        recipient: 'broken@mailtypo.co',
        reason: 'hard bounce · mailbox not found'
    },
    {
        id: 'nf2',
        ts: '13:42',
        channel: 'Email',
        template: 'release_notes',
        recipient: 'full@inbox.co',
        reason: 'soft bounce · mailbox full'
    },
    {
        id: 'nf3',
        ts: '12:18',
        channel: 'Electron native',
        template: 'comment_reply',
        recipient: 'usr_018',
        reason: 'OS notifications disabled'
    },
    {
        id: 'nf4',
        ts: '10:02',
        channel: 'Email',
        template: 'trial_ending',
        recipient: 'gone@forever.co',
        reason: 'hard bounce · address rejected'
    }
];

export const FIXTURE_BROADCASTS: BroadcastRow[] = [
    {
        id: 'b1',
        title: 'v2.5.0 release notes',
        audience: 'all · 12,847 users',
        count: 12847,
        progress_pct: 68,
        state: 'sending'
    },
    {
        id: 'b2',
        title: 'ToS v4.2 reminder · 7d warning',
        audience: 'pending accept · 1,542',
        count: 1542,
        progress_pct: 100,
        state: 'complete'
    },
    {
        id: 'b3',
        title: 'Scheduled April maintenance',
        audience: 'active · 7,120',
        count: 7120,
        progress_pct: 0,
        scheduled_at: '2026-04-21 02:00',
        state: 'queued'
    }
];

// ============================================================
// Restore
// ============================================================

export const FIXTURE_RESTORE_KPIS: RestoreKpis = {
    soft_deleted: 4218,
    restores_30d: 284,
    self_service_pct: 78.4,
    exports_pending: 8,
    split_sheets_30d: 28,
    snapshot_storage_gb: 412,
    hard_delete_queue_24h: 142
};

export const FIXTURE_SOFT_DELETES: SoftDeleteRow[] = [
    { type: 'Track groups', count: 1820 },
    { type: 'Individual tracks / bounces', count: 2184 },
    { type: 'Buckets', count: 82 },
    { type: 'Collaborators', count: 42 },
    { type: 'Comments', count: 42 },
    { type: 'Tags', count: 18 },
    { type: 'Shares', count: 30 }
];

export const FIXTURE_RESTORE_TIMELINE: RestoreTimelineRow[] = [
    {
        id: 'rt1',
        ts: '14:28',
        actor: 'miles@oaklab.studio',
        item_type: 'Track group',
        item_name: 'midnight_rain',
        reason: 'self_service'
    },
    {
        id: 'rt2',
        ts: '12:14',
        actor: 'admin: ross@glimbr.com',
        item_type: 'Bucket',
        item_name: 'Live EP',
        reason: 'via_support'
    },
    {
        id: 'rt3',
        ts: '10:42',
        actor: 'kelly.r@proton.me',
        item_type: 'Comment',
        item_name: '"try a half-time ride"',
        reason: 'self_service'
    },
    {
        id: 'rt4',
        ts: 'yday',
        actor: 'bea.osei@glimbr.com',
        item_type: 'Share',
        item_name: 'afterglow_demo',
        reason: 'self_service'
    }
];

export const FIXTURE_GDPR_EXPORTS: GdprExportRow[] = [
    {
        id: 'gx1',
        user_email: 'diane.v@mailbox.io',
        type: 'full_export',
        status: 'building',
        requested_at: '4h ago'
    },
    {
        id: 'gx2',
        user_email: 'chris@tyler.fm',
        type: 'full_export',
        status: 'ready',
        requested_at: '12h ago'
    },
    {
        id: 'gx3',
        user_email: 'fraud+test@mail.com',
        type: 'deletion',
        status: 'delivered',
        requested_at: '2d ago'
    },
    {
        id: 'gx4',
        user_email: 'stuck@sample.co',
        type: 'full_export',
        status: 'stuck',
        requested_at: '18d ago',
        stuck_days: 18
    }
];

// ============================================================
// Admin & audit
// ============================================================

export const FIXTURE_ADMIN_AUDIT_KPIS: AdminAuditKpis = {
    admins_total: 3,
    owners: 1,
    support: 2,
    read_only: 0,
    destructive_7d: 8,
    impersonations_7d: 2,
    admins_without_2fa: 0
};

export const FIXTURE_ADMINS: AdminUserRow[] = [
    {
        id: 'adm1',
        name: 'Ross Crispin',
        email: 'ross@glimbr.com',
        role: 'owner',
        last_active: 'just now',
        tfa_enabled: true,
        added_at: '2024-12-14'
    },
    {
        id: 'adm2',
        name: 'Aisha Patel',
        email: 'aisha@glimbr.com',
        role: 'support',
        last_active: '18m ago',
        tfa_enabled: true,
        added_at: '2025-06-02'
    },
    {
        id: 'adm3',
        name: 'Dex Morgan',
        email: 'dex@glimbr.com',
        role: 'support',
        last_active: '2h ago',
        tfa_enabled: true,
        added_at: '2025-11-18'
    }
];

export const FIXTURE_ADMIN_AUDIT: AdminAuditEntry[] = [
    {
        id: 'aa1',
        source: 'event',
        ts: '14:23',
        actor: 'ross@glimbr.com',
        actor_role: 'owner',
        action: 'user.suspend',
        target_type: 'user',
        target_id: 'fraud+test@mail.com',
        reason: 'fraud · duplicate card',
        severity: 'destructive',
        ip: '68.12.x.x'
    },
    {
        id: 'aa2',
        source: 'event',
        ts: '13:48',
        actor: 'aisha@glimbr.com',
        actor_role: 'support',
        action: 'user.impersonate',
        target_type: 'user',
        target_id: 'miles@oaklab.studio',
        reason: 'debug stuck import · ticket #PD-2514',
        severity: 'sensitive',
        ip: '202.18.x.x'
    },
    {
        id: 'aa3',
        source: 'event',
        ts: '12:14',
        actor: 'ross@glimbr.com',
        actor_role: 'owner',
        action: 'policy.publish',
        target_type: 'policy',
        target_id: 'tos-v4.2',
        reason: 'scheduled release',
        severity: 'sensitive',
        ip: '68.12.x.x'
    },
    {
        id: 'aa4',
        source: 'event',
        ts: '10:42',
        actor: 'dex@glimbr.com',
        actor_role: 'support',
        action: 'share.revoke',
        target_type: 'share',
        target_id: 'sh_mno345',
        reason: 'abuse report · copyright',
        severity: 'destructive',
        ip: '71.42.x.x'
    },
    {
        id: 'aa5',
        source: 'event',
        ts: 'yday',
        actor: 'ross@glimbr.com',
        actor_role: 'owner',
        action: 'master_key.rotate',
        target_type: 'master_key',
        target_id: 'v12',
        reason: '90-day scheduled rotation',
        severity: 'sensitive',
        ip: '68.12.x.x'
    }
];

export const FIXTURE_IMPERSONATIONS: ImpersonationLogEntry[] = [
    {
        id: 'im1',
        ts: '13:48',
        actor: 'aisha@glimbr.com',
        target_user: 'miles@oaklab.studio',
        duration_min: 12,
        reason: 'debug stuck import · ticket #PD-2514',
        actions_count: 4
    },
    {
        id: 'im2',
        ts: 'yday',
        actor: 'dex@glimbr.com',
        target_user: 'nia@indie-lab.com',
        duration_min: 6,
        reason: 'reproduce BPM wrong · ticket #PD-2507',
        actions_count: 2
    }
];

// ------- Analytics (extended) — per docs/specs/analytics.md §5.3, §6.2, §6.3 -------

import type { ConversionTimeBucket, PerFeatureFunnelSteps, PaidVsFreeRow } from './types';

export const FIXTURE_CONVERSION_TIME: ConversionTimeBucket[] = [
    { bucket: 'same_day', label: 'Same day', count: 14, pct: 11.3 },
    { bucket: '7d', label: '≤ 7 days', count: 22, pct: 17.7 },
    { bucket: '14d', label: '≤ 14 days', count: 31, pct: 25.0 },
    { bucket: '30d', label: '≤ 30 days', count: 28, pct: 22.6 },
    { bucket: '60d', label: '≤ 60 days', count: 16, pct: 12.9 },
    { bucket: '90d', label: '≤ 90 days', count: 8, pct: 6.5 },
    { bucket: 'over_90d', label: '> 90 days', count: 5, pct: 4.0 }
];

export const FIXTURE_CONVERSION_MEDIAN_DAYS = 14;

export const FIXTURE_PER_FEATURE_FUNNELS: PerFeatureFunnelSteps[] = [
    {
        feature: 'Focus mode',
        retention_pct: 38.4,
        steps: [
            { step: 'Discovered', count: 2840, pct: 100, dropoff: 0 },
            { step: 'Tried', count: 1812, pct: 63.8, dropoff: 1028 },
            { step: 'Used 3×', count: 1406, pct: 49.5, dropoff: 406 },
            { step: 'Regular user', count: 1090, pct: 38.4, dropoff: 316 }
        ]
    },
    {
        feature: 'AI chat',
        retention_pct: 27.1,
        steps: [
            { step: 'Discovered', count: 3108, pct: 100, dropoff: 0 },
            { step: 'Tried', count: 1946, pct: 62.6, dropoff: 1162 },
            { step: 'Used 3×', count: 1204, pct: 38.7, dropoff: 742 },
            { step: 'Regular user', count: 842, pct: 27.1, dropoff: 362 }
        ]
    },
    {
        feature: 'Share playlists',
        retention_pct: 44.2,
        steps: [
            { step: 'Discovered', count: 2218, pct: 100, dropoff: 0 },
            { step: 'Tried', count: 1612, pct: 72.7, dropoff: 606 },
            { step: 'Used 3×', count: 1184, pct: 53.4, dropoff: 428 },
            { step: 'Regular user', count: 980, pct: 44.2, dropoff: 204 }
        ]
    },
    {
        feature: 'Licensing marketplace',
        retention_pct: 9.6,
        steps: [
            { step: 'Discovered', count: 1842, pct: 100, dropoff: 0 },
            { step: 'Tried', count: 612, pct: 33.2, dropoff: 1230 },
            { step: 'Used 3×', count: 284, pct: 15.4, dropoff: 328 },
            { step: 'Regular user', count: 176, pct: 9.6, dropoff: 108 }
        ]
    }
];

export const FIXTURE_PAID_VS_FREE: PaidVsFreeRow[] = [
    { cohort: '2025-12', paid_w8: 84, free_w8: 32 },
    { cohort: '2026-01', paid_w8: 87, free_w8: 36 },
    { cohort: '2026-02', paid_w8: 89, free_w8: 38 },
    { cohort: '2026-03', paid_w8: 91, free_w8: 42 },
    { cohort: '2026-04', paid_w8: 92, free_w8: 44 }
];

// ------- Feature flag overrides + history — per docs/specs/feature-flags.md §5.2 -------

import type { FlagOverride, FlagHistoryEntry } from './types';

export const FIXTURE_FLAG_OVERRIDES: Record<string, FlagOverride[]> = {
    ai_chat_v2: [
        {
            user_id: 'usr_001',
            user_email: 'miles@oaklab.studio',
            value: true,
            added_by: 'ross@glimbr.com',
            added_at: '2026-04-12'
        },
        {
            user_id: 'usr_034',
            user_email: 'nia@indie-lab.com',
            value: false,
            added_by: 'ross@glimbr.com',
            added_at: '2026-04-10'
        }
    ],
    licensing_marketplace_v1: [
        {
            user_id: 'usr_012',
            user_email: 'bea.osei@glimbr.com',
            value: true,
            added_by: 'ross@glimbr.com',
            added_at: '2026-04-16'
        },
        {
            user_id: 'usr_048',
            user_email: 'rob@crestfield.co',
            value: true,
            added_by: 'dex@glimbr.com',
            added_at: '2026-04-15'
        },
        {
            user_id: 'usr_117',
            user_email: 'ivy@forestgreen.io',
            value: true,
            added_by: 'ross@glimbr.com',
            added_at: '2026-04-14'
        },
        {
            user_id: 'usr_204',
            user_email: 'jun@signalbox.co',
            value: false,
            added_by: 'dex@glimbr.com',
            added_at: '2026-04-13'
        },
        {
            user_id: 'usr_331',
            user_email: 'theo@northroad.fm',
            value: true,
            added_by: 'ross@glimbr.com',
            added_at: '2026-04-11'
        }
    ],
    focus_mode_haptics: [
        {
            user_id: 'usr_002',
            user_email: 'dex@glimbr.com',
            value: true,
            added_by: 'ross@glimbr.com',
            added_at: '2026-04-08'
        },
        {
            user_id: 'usr_003',
            user_email: 'ross@glimbr.com',
            value: true,
            added_by: 'ross@glimbr.com',
            added_at: '2026-04-08'
        },
        {
            user_id: 'usr_004',
            user_email: 'kelly.r@proton.me',
            value: true,
            added_by: 'ross@glimbr.com',
            added_at: '2026-04-05'
        }
    ],
    public_profile_beta: [
        {
            user_id: 'usr_019',
            user_email: 'sam.harlow@vinylcrate.com',
            value: true,
            added_by: 'ross@glimbr.com',
            added_at: '2026-04-09'
        }
    ]
};

export const FIXTURE_FLAG_HISTORY: Record<string, FlagHistoryEntry[]> = {
    ai_chat_v2: [
        {
            id: 'fh1',
            ts: '2026-04-18 09:14',
            actor: 'ross@glimbr.com',
            change: 'Rollout %',
            before: '25',
            after: '40'
        },
        {
            id: 'fh2',
            ts: '2026-04-14 16:02',
            actor: 'ross@glimbr.com',
            change: 'Added target plans',
            before: 'pro',
            after: 'pro, studio, team'
        },
        {
            id: 'fh3',
            ts: '2026-04-12 11:21',
            actor: 'ross@glimbr.com',
            change: 'Added override',
            after: 'miles@oaklab.studio → on'
        },
        {
            id: 'fh4',
            ts: '2026-04-08 10:00',
            actor: 'dex@glimbr.com',
            change: 'Status',
            before: 'internal',
            after: 'rolling_out'
        },
        {
            id: 'fh5',
            ts: '2026-03-30 14:48',
            actor: 'ross@glimbr.com',
            change: 'Created flag'
        }
    ],
    licensing_marketplace_v1: [
        {
            id: 'fh6',
            ts: '2026-04-17 12:04',
            actor: 'ross@glimbr.com',
            change: 'Rollout %',
            before: '5',
            after: '10'
        },
        {
            id: 'fh7',
            ts: '2026-04-16 09:30',
            actor: 'ross@glimbr.com',
            change: 'Added override',
            after: 'bea.osei@glimbr.com → on'
        },
        {
            id: 'fh8',
            ts: '2026-04-10 18:00',
            actor: 'dex@glimbr.com',
            change: 'Status',
            before: 'draft',
            after: 'rolling_out'
        },
        {
            id: 'fh9',
            ts: '2026-04-02 08:12',
            actor: 'ross@glimbr.com',
            change: 'Created flag'
        }
    ],
    focus_mode_haptics: [
        {
            id: 'fh10',
            ts: '2026-04-08 15:40',
            actor: 'ross@glimbr.com',
            change: 'Added override',
            after: '3 internal testers'
        },
        {
            id: 'fh11',
            ts: '2026-04-05 11:22',
            actor: 'ross@glimbr.com',
            change: 'Created flag'
        }
    ],
    byok_openai_gpt_5_4: [
        {
            id: 'fh12',
            ts: '2026-04-12 14:02',
            actor: 'ross@glimbr.com',
            change: 'Rollout %',
            before: '60',
            after: '100'
        },
        {
            id: 'fh13',
            ts: '2026-04-04 09:20',
            actor: 'dex@glimbr.com',
            change: 'Status',
            before: 'rolling_out',
            after: 'on'
        }
    ],
    public_profile_beta: [
        {
            id: 'fh14',
            ts: '2026-04-15 10:14',
            actor: 'ross@glimbr.com',
            change: 'Rollout %',
            before: '10',
            after: '25'
        },
        {
            id: 'fh15',
            ts: '2026-04-09 12:00',
            actor: 'ross@glimbr.com',
            change: 'Added override',
            after: 'sam.harlow@vinylcrate.com → on'
        }
    ],
    split_sheets_docusign: [
        {
            id: 'fh16',
            ts: '2026-04-08 16:30',
            actor: 'ross@glimbr.com',
            change: 'Created flag'
        }
    ]
};

// ------- Changelog full bodies — per docs/specs/changelog.md §5.2 -------

export const FIXTURE_CHANGELOG_BODIES: Record<string, string> = {
    cl1: `## Highlights\n- Public licensing marketplace storefront\n- Stripe Connect onboarding for payouts\n- Split-sheet signatures via DocuSign\n\n## Breaking\n- Legacy \`/api/licenses/*\` endpoints return **410 Gone**; migrate to \`/api/marketplace/*\`\n\n## Fixed\n- Dropbox refresh-token 401 loop`,
    cl2: `## Highlights\n- **Focus mode** — disappear into a single track\n- **BYOK** now supports GPT-5.4 keys\n- Trackpad haptics on section transitions (opt-in)\n\n## Under the hood\n- Waveform v2 engine (2x faster paint)\n- Chat threads persisted across sessions`,
    cl3: `## Fixed\n- Import pipeline 40% faster on long sessions\n- Dropbox 401s now auto-recover without user action\n- BPM detection no longer returns half-step on 170+ BPM tracks\n\n## Under the hood\n- Essentia bumped to 2.1-beta2\n- Supabase pool tuning`,
    cl4: `## Highlights\n- **Collaborators** on specific buckets with per-bucket permissions\n- **Inline comments** on any track in a share page\n- Real-time presence indicators\n\n## Fixed\n- Playlist drag handle alignment on Windows`,
    cl5: `## Scheduled maintenance\n\nShort Supabase maintenance window **Sunday 21 April 02:00 UTC**.\n\n- Duration ≈ 15 minutes\n- API will return 503 during the window\n- Import queue will resume automatically\n\nNo user action required.`
};

// ------- Licensing — payout-run preview (appended 2026-04-19) -------
// Per docs/specs/licensing.md §7 — eligible accounts, total $, held/failed
// breakdown. Consumed by `usePayoutPreview()` and the payout-run modal.
export const FIXTURE_PAYOUT_RUN_PREVIEW: import('./types').PayoutRunPreview = {
    eligible_accounts: 12,
    held_w9: 3,
    held_kyc: 2,
    failed: 1,
    total_amount: 1284,
    items: [
        { licensor: 'producer@glimbr.com', amount: 398 },
        { licensor: 'kelly.r@proton.me', amount: 299 },
        { licensor: 'miles@oaklab.studio', amount: 240 },
        { licensor: 'bea.osei@glimbr.com', amount: 149 },
        { licensor: 'vera@holbrook.studio', amount: 80 },
        { licensor: 'leo@sparrowfm.co', amount: 68 },
        { licensor: 'june@northblvd.studio', amount: 50 }
    ]
};

// ------- Help Docs — regeneration cost stub (appended 2026-04-19) -------
// Per docs/specs/help-docs.md §6 — consumed by the regen confirmation modals
// to render expected cost (`HELP_REGEN_COST_PER_DOC * N`).
export const HELP_REGEN_COST_PER_DOC = 0.024;

// ------- Restore (extended) — per docs/specs/restore.md §5.4, §6 -------

import type { SnapshotRow, SplitSheetStats } from './types';

export const FIXTURE_SNAPSHOTS: SnapshotRow[] = [
    {
        id: 'snap_2026_04_18',
        created_at: '2026-04-18 02:00',
        size_gb: 84.2,
        users_covered: 12847,
        retention_days: 90
    },
    {
        id: 'snap_2026_04_11',
        created_at: '2026-04-11 02:00',
        size_gb: 81.7,
        users_covered: 12612,
        retention_days: 83
    },
    {
        id: 'snap_2026_04_04',
        created_at: '2026-04-04 02:00',
        size_gb: 78.4,
        users_covered: 12384,
        retention_days: 76
    },
    {
        id: 'snap_2026_03_28',
        created_at: '2026-03-28 02:00',
        size_gb: 74.1,
        users_covered: 12148,
        retention_days: 69
    },
    {
        id: 'snap_2026_03_21',
        created_at: '2026-03-21 02:00',
        size_gb: 71.8,
        users_covered: 11928,
        retention_days: 62
    },
    {
        id: 'snap_2026_03_14',
        created_at: '2026-03-14 02:00',
        size_gb: 68.2,
        users_covered: 11702,
        retention_days: 55
    }
];

export const FIXTURE_SPLIT_SHEET_STATS: SplitSheetStats = {
    generated_30d: 28,
    signed_via_docusign: 21,
    pending_signature: 5,
    avg_collaborators: 2.4,
    most_common_dispute: 'producer credit %'
};

// ------- Background jobs (extended) — per docs/specs/background-jobs.md §6.4 -------

import type { JobRun } from './types';

function runsFor(name: string, seed: number): JobRun[] {
    const failHit = seed % 7;
    return Array.from({ length: 12 }, (_, i) => {
        const idx = 11 - i;
        const fail = idx === failHit;
        const stuck = idx === 1 && seed % 5 === 0;
        const duration = 400 + Math.round(Math.abs(Math.sin(seed + idx) * 4200));
        return {
            id: `${name}-r${idx}`,
            started_at: `${String(Math.max(0, 23 - i)).padStart(2, '0')}:${String((i * 7) % 60).padStart(2, '0')}`,
            finished_at: stuck
                ? undefined
                : `${String(Math.max(0, 23 - i)).padStart(2, '0')}:${String(((i * 7) % 60) + 1).padStart(2, '0')}`,
            duration_ms: stuck ? 620000 : duration,
            status: stuck ? 'stuck' : fail ? 'failed' : 'succeeded',
            output_summary: fail
                ? undefined
                : `processed ${Math.round(10 + Math.abs(Math.cos(seed + idx)) * 140)} items · 0 errors`,
            error: fail ? `Transient upstream 502 · retried 3× · gave up` : undefined,
            trigger: i === 0 && seed % 4 === 0 ? 'manual' : 'cron'
        } as JobRun;
    });
}

export const FIXTURE_JOB_RUNS: Record<string, JobRun[]> = {
    'analysis-watchdog': runsFor('analysis-watchdog', 1),
    'recover-stuck-analysis': runsFor('recover-stuck-analysis', 2),
    'refresh-tokens': runsFor('refresh-tokens', 3),
    'process-data-exports': runsFor('process-data-exports', 4),
    'process-collaboration-imports': runsFor('process-collaboration-imports', 5),
    'cleanup-artwork': runsFor('cleanup-artwork', 6),
    'cleanup-orphaned-images': runsFor('cleanup-orphaned-images', 7),
    'check-share-acceptance': runsFor('check-share-acceptance', 8)
};

// ------- System health (extended) — per docs/specs/system-health.md §8 -------

import type { HealthEvent } from './types';

export const FIXTURE_HEALTH_EVENTS: HealthEvent[] = [
    {
        id: 'he1',
        ts: '14:42',
        integration_id: 'stripe',
        from_status: 'healthy',
        to_status: 'degraded',
        note: 'Webhook delivery error rate > 20% · auto-alert fired to #ops'
    },
    {
        id: 'he2',
        ts: '13:18',
        integration_id: 'openai',
        from_status: 'healthy',
        to_status: 'elevated',
        note: 'p95 climbed to 1.2s — provider status page reports elevated latency'
    },
    {
        id: 'he3',
        ts: '11:04',
        integration_id: 'anthropic',
        from_status: 'degraded',
        to_status: 'healthy',
        note: 'Auto-recovered after provider-side fix'
    },
    {
        id: 'he4',
        ts: '09:28',
        integration_id: 'dropbox',
        from_status: 'elevated',
        to_status: 'healthy',
        note: 'Token-refresh batch error cleared after retry'
    },
    {
        id: 'he5',
        ts: 'yday 23:50',
        integration_id: 'stripe',
        from_status: 'degraded',
        to_status: 'healthy',
        note: 'Temporary — flipped red again at 14:42 today'
    },
    {
        id: 'he6',
        ts: 'yday 18:12',
        integration_id: 'r2',
        from_status: 'healthy',
        to_status: 'elevated',
        note: 'Brief EU-west spike · no user impact reported'
    },
    {
        id: 'he7',
        ts: 'yday 18:20',
        integration_id: 'r2',
        from_status: 'elevated',
        to_status: 'healthy',
        note: 'Recovered within one probe cycle'
    },
    {
        id: 'he8',
        ts: 'yday 09:41',
        integration_id: 'cloudflare-email',
        from_status: 'healthy',
        to_status: 'elevated',
        note: 'Marketing-broadcast flush pushed sustained rate above threshold'
    },
    {
        id: 'he9',
        ts: '2d ago',
        integration_id: 'worker-docs',
        from_status: 'healthy',
        to_status: 'degraded',
        note: 'Deploy bad build · rolled back within 3m'
    },
    {
        id: 'he10',
        ts: '2d ago',
        integration_id: 'worker-docs',
        from_status: 'degraded',
        to_status: 'healthy',
        note: 'Rollback succeeded'
    }
];

// ============================================================
// Import prompt history — per docs/specs/import-pipeline.md §7
// ============================================================

import type { ImportPromptVersion, TaxonomyMigration } from './types';

export const FIXTURE_IMPORT_PROMPT_HISTORY: Record<
    'review' | 'pattern-discovery' | 'display-name-cleaner',
    ImportPromptVersion[]
> = {
    review: [
        {
            id: 'ipv_r_v7',
            version: 'v7',
            model: 'Claude Sonnet 4.6',
            temp: 0.2,
            created_at: '2026-04-11 09:42',
            active: true,
            body: 'You are reviewing a proposed grouping of audio files into track groups. The naming pattern detected is {{pattern}}. Groups:\n{{groups}}\n\nFor each group, evaluate: are these files the same song? Any misgroupings? Return JSON with corrections.'
        },
        {
            id: 'ipv_r_v6',
            version: 'v6',
            model: 'Claude Sonnet 4.6',
            temp: 0.3,
            created_at: '2026-03-28 15:14',
            active: false,
            body: 'You are reviewing a proposed grouping of audio files. Pattern: {{pattern}}. Groups:\n{{groups}}\nReturn JSON corrections.'
        },
        {
            id: 'ipv_r_v5',
            version: 'v5',
            model: 'Claude Sonnet 4.5',
            temp: 0.3,
            created_at: '2026-02-14 11:08',
            active: false,
            body: 'Review this grouping: {{groups}} — return JSON.'
        },
        {
            id: 'ipv_r_v4',
            version: 'v4',
            model: 'Claude Sonnet 4.5',
            temp: 0.4,
            created_at: '2026-01-08 08:20',
            active: false,
            body: 'Review proposed audio file grouping. Groups:\n{{groups}}\nReturn corrections.'
        }
    ],
    'pattern-discovery': [
        {
            id: 'ipv_p_v4',
            version: 'v4',
            model: 'Claude Haiku 4.5',
            temp: 0.1,
            created_at: '2026-04-02 10:18',
            active: true,
            body: 'Analyse these filenames for a consistent naming pattern:\n{{filenames}}\n\nReturn a JSON pattern spec with delimiters, version tokens, and confidence.'
        },
        {
            id: 'ipv_p_v3',
            version: 'v3',
            model: 'Claude Haiku 4.5',
            temp: 0.2,
            created_at: '2026-02-20 16:42',
            active: false,
            body: 'Detect filename pattern: {{filenames}}. Return JSON.'
        },
        {
            id: 'ipv_p_v2',
            version: 'v2',
            model: 'GPT-5.4 mini',
            temp: 0.2,
            created_at: '2026-01-15 12:04',
            active: false,
            body: 'Find the naming pattern in {{filenames}}.'
        }
    ],
    'display-name-cleaner': [
        {
            id: 'ipv_d_v3',
            version: 'v3',
            model: 'Claude Haiku 4.5',
            temp: 0.1,
            created_at: '2026-03-18 14:28',
            active: true,
            body: 'Given this raw filename: {{filename}}, return a clean, title-cased display name with underscores and technical suffixes removed.'
        },
        {
            id: 'ipv_d_v2',
            version: 'v2',
            model: 'Claude Haiku 4.5',
            temp: 0.2,
            created_at: '2026-02-04 09:52',
            active: false,
            body: 'Clean filename: {{filename}} → title case, strip suffixes.'
        },
        {
            id: 'ipv_d_v1',
            version: 'v1',
            model: 'GPT-5.4 mini',
            temp: 0.2,
            created_at: '2026-01-02 10:10',
            active: false,
            body: 'Title-case this: {{filename}}'
        }
    ]
};

// ============================================================
// Taxonomy migration history — per docs/specs/taxonomy.md §7
// ============================================================

export const FIXTURE_TAXONOMY_MIGRATIONS: TaxonomyMigration[] = [
    {
        id: 'tmig1',
        ts: '2026-04-14 11:08',
        actor: 'ross@glimbr.com',
        change: 'Renamed stage "Tracking" → "Record"',
        scope: 'new_users_only',
        affected_users: 0,
        rollback_available: true
    },
    {
        id: 'tmig2',
        ts: '2026-04-02 14:32',
        actor: 'aisha@glimbr.com',
        change: 'Added standard tag "lofi" to genre',
        scope: 'retro_untouched',
        affected_users: 2812,
        rollback_available: true
    },
    {
        id: 'tmig3',
        ts: '2026-03-24 09:14',
        actor: 'ross@glimbr.com',
        change: 'Retired workflow "Sync-first" (replaced by "Sync brief")',
        scope: 'retro_all',
        affected_users: 184,
        rollback_available: true
    },
    {
        id: 'tmig4',
        ts: '2026-03-10 16:48',
        actor: 'ross@glimbr.com',
        change: 'Reordered stages: Demo before Arrangement',
        scope: 'new_users_only',
        affected_users: 0,
        rollback_available: true
    },
    {
        id: 'tmig5',
        ts: '2026-02-18 10:22',
        actor: 'aisha@glimbr.com',
        change: 'Added "Archived" stage with colour --text-dim',
        scope: 'retro_untouched',
        affected_users: 4182,
        rollback_available: false
    },
    {
        id: 'tmig6',
        ts: '2026-02-04 12:02',
        actor: 'ross@glimbr.com',
        change: 'Updated tag palette: "chill" → --cyan',
        scope: 'retro_all',
        affected_users: 8420,
        rollback_available: true
    }
];

// ============================================================
// Audio failure telemetry — per docs/specs/audio.md §5.5 / §6.
// Non-breaking: attaches optional payloads onto existing rows.
// ============================================================

FIXTURE_AUDIO_FAILURES[0].telemetry_payload = {
    decode_context: 'ffmpeg-wasm · m4a → pcm16 · sample_rate=44100',
    wasm_heap_used: '142 MB / 256 MB',
    last_essentia_op: 'LoudnessEBUR128 → KeyExtractor',
    stack_trace:
        'at EssentiaJS.decode (essentia.js:8241)\n  at AudioWorker.analyse (worker.js:184)\n  at onmessage (worker.js:42)',
    codec_profile: 'alac',
    os: 'macOS 15.2 (arm64)'
};

FIXTURE_AUDIO_FAILURES[1].telemetry_payload = {
    decode_context: 'web-audio · wav 24-bit stereo · 48kHz',
    wasm_heap_used: '248 MB / 256 MB (OOM)',
    last_essentia_op: 'PercivalBpmEstimator',
    stack_trace:
        'RuntimeError: memory access out of bounds\n  at wasm-function[412]\n  at EssentiaJS.extractBPM (essentia.js:4128)',
    file_size_mb: '184.2',
    os: 'Windows 11 (22H2)'
};

FIXTURE_AUDIO_FAILURES[2].telemetry_payload = {
    decode_context: 'aiff parser · header read failed at byte 12',
    wasm_heap_used: '18 MB / 256 MB',
    last_essentia_op: 'HeaderRead (pre-decode)',
    stack_trace:
        'Error: invalid AIFF COMM chunk\n  at parseAIFF (aiff-parser.js:82)\n  at ImportQueue.decode (import.js:204)',
    header_bytes: '46 4F 52 4D 00 00 00 00 00 00 00 00',
    os: 'macOS 14.6 (intel)'
};
